import type { ZarplataStroka } from '../complaints/api';
import { apiClient } from '../../api/client';

/**
 * ТИПЫ И ЗАПРОСЫ ВОСЬМИ ЭКРАНОВ ЗАПИСИ Э-З1…Э-З8 (`s303`, TZ-R-zapis §4.9).
 *
 * ⛔ ВСЁ, ЧТО ЧИТАЕТ ЧЕЛОВЕК, ПРИХОДИТ ГОТОВОЙ СТРОКОЙ. Поля с хвостом
 * `_display` — уже собранная сервером фраза: «14:00–15:30», «подтвердила»,
 * «7 380 ₽». Экрану складывать и форматировать запрещено сторожем: собери он
 * рубли сам — телефон и десктоп однажды написали бы одну сумму по-разному.
 *
 * ⛔ `null` — ЭТО ПУСТОТА, А НЕ «НЕТ ДАННЫХ». Сервер не подставляет «—»:
 * слово про пустоту экран берёт из словаря (`words.texts`), а не выдумывает.
 */

/** Подсказка «?» у поля: ключ → текст; `null` — строки в словаре нет. */
export type Hints = Record<string, string | null>;

export interface ScreenWords {
  labels: Record<string, string>;
  texts: Record<string, string>;
}

export interface Column {
  key: string;
  label: string;
}

export interface CheckBrief {
  amount_known?: boolean;
  id: number;
  status_display: string | null;
  /** Раскрытое состояние чека (Плашка №8, §6/§13.1) — единственная истина `state → значок`. */
  payment_state: string | null;
  payment_state_display: string | null;
  /** «Чек переоткрыт» (Плашка №8) — признак, а не сравнение `status_display` со словом. */
  reopened?: boolean;
  k_oplate_display: string;
  paid_display: string;
  /**
   * ⛔ ДОЛГ — ТОЛЬКО КОГДА ОН ЕСТЬ (`s319`, З-13). Сервер отдавал это поле с
   * `s305`, а тип его не знал — и строка «Незакрытого» показывала «к оплате» и
   * «внесено» двумя числами, оставляя вычитание глазу читателя (op-rules §31.6:
   * две величины рядом — не ответ). Ноль остаётся пустотой: «0 ₽» владелец
   * читает как «всё сошлось».
   */
  dolg_display: string | null;
  services_display: string | null;
}

/** Одна запись в словах экрана — общий вид для всех восьми экранов. */
/**
 * Исходы, которые ставятся прямо с плитки журнала (`s320`, Д7/Д32).
 *
 * ⛔ Сам СПИСОК живых ходов приходит с сервера (`row.perehody`) — этот союз
 * лишь называет те, у которых на плитке есть своя кнопка. Второй таблицы
 * разрешённых переходов на фронте не заводим (`notes.md` Э-З2).
 */
export type IshodHod = 'arrive' | 'no_show' | 'cancel' | 'otkat';

export interface ZapisRow {
  /** `s960`: жалоба исходного визита или гарантийной записи. */
  zhaloba?: { id: number; state: string; state_display: string; guarantee_booking_id: number | null; zarplata?: ZarplataStroka[] | null } | null;
  id: number;
  state: string;
  state_display: string | null;
  is_active: boolean;

  /**
   * СТАТУС ЗАПИСИ ДЛЯ ВИДА — шкала цвета плитки (решение владельца 09.09.2026).
   *
   * ⛔ ЭТО НЕ `state`. Состояние машины отвечает «какие ходы живы», статус вида —
   * «какого цвета плитка»: у «закрыт» цвета нет вовсе, а у «подтвердила» цветов
   * три — по ступени подтверждения. Красить плитку по `state` значило бы вернуть
   * слияние «пришла» с «закрыт» и «не пришла» с «отменена».
   */
  status_vida: string;
  status_vida_display: string | null;
  /** Ступень подтверждения (`deposit` | `replied` | `called`) или пусто. */
  stupen?: string | null;
  stupen_display?: string | null;

  /**
   * ПОДСТАТУС ПОДТВЕРЖДЕНИЯ (`s324`): просто подтверждена · ответила на рассылку
   * · подтвердила звонком накануне. Приезжает ГОТОВЫМ: старшинство трёх
   * источников (рука администратора → пометка вендора → канал подтверждения)
   * считает сервер одним слоем, и повторять его здесь значило бы завести второй
   * ответ на тот же вопрос.
   */
  podstatus?: string | null;
  podstatus_display?: string | null;
  /** Пометка стоит РУКОЙ — ночной забор её не тронет. */
  podstatus_ruka?: boolean;
  podstatus_varianty?: { kod: string; display: string | null }[];
  /** «Принята без брони» рукой (`s328`, Ф1) — открыт ТОЛЬКО до подтверждения. */
  prinyata_ruka?: boolean;
  mozhno_prinyat_bez_broni?: boolean;
  /** «500 ₽ · 16.09» — бронь, внесённая у вендора; NULL — не внесена. */
  vendor_bron_display?: string | null;
  /** Значки плитки: закрытие, оплата и происхождение — то, что не показывается цветом. */
  znachki?: TileZnachok[];

  /**
   * МОЖНО ЛИ ЗАКРЫТЬ ВИЗИТ ПРЯМО СО СТРОКИ (`s319`, З-13, Б2.06).
   *
   * ⛔ ОТВЕТ ПРИЕЗЖАЕТ ГОТОВЫМ, ВМЕСТЕ С ПРИЧИНОЙ. Повтори условия гейта на
   * экране — и у вопроса «можно ли» появится второй ответ: кнопка живая, машина
   * отказывает. Причина обязательна: серая кнопка без причины читается как
   * поломка (О-25).
   */
  zakrytie?: { mozhno: boolean; prichina_display: string | null };
  /** Можно ли передать долг управляющей (Б2.05, ход `escalate` с причиной). */
  mozhno_escalate?: boolean;

  /**
   * День записи — ключ группировки шкалы «Незакрытого» (О-13, вариант «А»).
   *
   * ⛔ ДВА ПОЛЯ, А НЕ ОДНО: `den` машинный (ключ группы), `den_display` —
   * готовая подпись с сервера. Собери подпись из ключа на экране — и появится
   * второй разбор даты рядом с серверным.
   */
  den?: string | null;
  den_display?: string | null;

  slot_display: string | null;
  /** Длительность словами: «1 час 30 минут» (V2.08). Считает сервер. */
  slot_duration_display?: string | null;
  slot_date_display: string | null;
  /**
   * Дата словами для шапки карточки: «20 сентября 2026» (`s321`, В1, Д36).
   * Считает сервер (`Velichiny::dataSlovami`) — второго разбора даты на фронте
   * не заводим.
   */
  slot_date_slovami?: string | null;
  /** `s322` Д25: мастер числом — им спрашивают занятость его дня. */
  staff_id?: number | null;
  staff_display: string | null;
  /** Лицо и должность мастера — как в шапке колонки журнала (V2.04/V2.06). */
  staff_avatar?: string | null;
  staff_initials?: string | null;
  staff_position_display?: string | null;
  /** Служебная обёртка вендора «Лист ожидания», а не человек (решение штаба 3). */
  staff_is_pseudo: boolean;
  master_preference_display: string | null;
  /** «К своему» (Плашка №8, §7) — признак, а не сравнение display-строки. */
  master_preference_own?: boolean;

  channel_display: string | null;
  created_at_display: string | null;
  created_by_display: string | null;

  confirmed_at_display: string | null;
  confirmed_via_display: string | null;
  arrived_at_display: string | null;
  arrived_late_mark: boolean;
  closed_at_display: string | null;

  /** День и час порознь: ход визита ставит час отдельным столбцом. */
  confirmed_time_display: string | null;
  confirmed_date_display: string | null;
  arrived_time_display: string | null;
  arrived_date_display: string | null;
  closed_time_display: string | null;
  closed_date_display: string | null;

  outcome_at_display: string | null;
  outcome_reason_display: string | null;
  outcome_initiated_by_display: string | null;
  outcome_note: string | null;
  is_late_cancel: boolean;

  deposit_status_display: string | null;
  /**
   * В-10 (`s406`): показывать ли поля брони вовсе. Решает сервер: «бронь не
   * нужна» / «освобождена» / «день в день» — полей нет, состояние уже ответило.
   */
  deposit_polya_pokazat?: boolean;
  /**
   * Н-28: можно ли сейчас отсрочить бронь. Решает СЕРВЕР, а не экран по коду
   * статуса: сырые коды записи на фронт не выходят вовсе (сторож договора
   * экранов, `s303`), и «awaiting || overdue» было бы вторым ответом на
   * вопрос, который уже решает машина.
   */
  mozhno_otsrochit?: boolean;
  /**
   * `s322` Д61-3: бронь отсрочена — след решения стоит там, где было действие.
   * Признак, а не код: слово статуса владелец правит в справочнике.
   */
  bron_otsrochena?: boolean;
  deposit_amount_display: string | null;
  deposit_hold_display: string | null;
  deposit_due_at_display: string | null;
  /** ДД.ММ для «Бронь до ДД.ММ» (Плашка №8, §6.2) — короткий формат той же даты. */
  deposit_due_short_display?: string | null;
  /** Н-29: «кто освободил от брони» — имя, дата и причина одной фразой с сервера. */
  deposit_exception_by_display?: string | null;
  payment_control_display: string | null;
  /** Н-17: что решили с долгом — «Бартер» / «Прощено» / «Возврат». */
  debt_resolution_display?: string | null;
  /** Н-17: «Решил Ксения, 10.09.2026: обменяли на съёмку» — готовая фраза. */
  debt_resolution_sled_display?: string | null;
  /** Н-17: показывать ли кнопки решения. Замок — на трубе, это только видимость. */
  mozhno_reshit_dolg?: boolean;
  booked_price_display: string | null;

  transfer_no: number;
  transfer_no_display: string | null;
  /** Н-12: «Это будет 3-й перенос этой записи» — готовая фраза для диалога переноса. */
  sleduyushchiy_perenos_display?: string | null;
  /** Н-13: отмена прямо сейчас была бы поздней — признак для вида. */
  otmena_budet_pozdney?: boolean;
  /** Н-13: «До визита 1 ч 40 мин — отмена будет поздней…» — готовая фраза. */
  otmena_pozdnyaya_display?: string | null;
  prebooking_at_display: string | null;

  wants_earlier: boolean;
  wanted_window_display: string | null;
  /**
   * ⛔ НОРМА ДЛИТЕЛЬНОСТИ ВИЗИТА (`s341`, п.6 «СТРОИТЬ» `s340`). Считает свод
   * `NormaDlitelnosti`: сумма норм процедур состава, а где состава нет —
   * интервал мастера. `null` значит «судить не по чему», а НЕ «совпадает»:
   * нормы нет у 44 процедур из 110 и у 48 мастеров из 98.
   *
   * ⛔ ПРИХОДИТ ТОЛЬКО В КАРТОЧКЕ, не в строках списков: там её никто не
   * читает, а запрос за составом на каждую строку дал бы N+1 (поймано сторожем).
   */
  norma_minut?: number | null;
  norma_display?: string | null;
  /** Насколько визит длиннее или короче нормы. `null` — не судим или сошлось. */
  otklonenie_ot_normy_minut?: number | null;
  otklonenie_ot_normy_display?: string | null;
  /**
   * ⛔ ОКНО ПОЖЕЛАНИЯ УЖЕ ПОЗАДИ (`s341`, Н14). Считает сервер по ХВОСТУ окна:
   * «16.09–25.09», начавшееся на прошлой неделе, всё ещё живое. Экран рисует
   * признак, слово берёт из словаря — цветом одним не обойтись: серое читалось
   * бы и как «прошло», и как «не заполнено».
   */
  wanted_window_proshlo?: boolean;

  is_guarantee: boolean;
  /** `s960`: исходный визит, по которому решили переделать по гарантии — розовая рамка в журнале. */
  ushla_v_garantiyu?: boolean;
  /** Гарантия: признак И ссылка на исходный визит (разбор 3 §2.2). */
  guarantee_for_check_id?: number | null;
  guarantee_deadline_display?: string | null;
  is_locked: boolean;
  /** День записи машинной датой — под карточкой открывается журнал этого дня. */
  slot_date?: string | null;
  /** Длительность в минутах — перенос по сетке обязан её сохранить (`s311`). */
  slot_minut?: number | null;
  /** Машинное время начала/конца для поля правки (V2.09) — не для показа. */
  slot_start_input?: string | null;
  /** «Визит уже начался» — считает сервер по своим часам (Н-42, `s317`). */
  slot_nachalsya?: boolean;
  /**
   * ⛔ `s320` Д7: ХОДЫ МАШИНЫ ЕДУТ И В СТРОКУ ЖУРНАЛА. Плитка держала свой
   * список исходов в разметке и расходилась с карточкой; теперь у обеих один
   * источник — таблица переходов на сервере.
   */
  perehody?: Perehod[];
  slot_end_input?: string | null;
  filial_id?: number | null;
  /** Канал первого касания снимком на записи (разбор 3 §2.2). */
  lead_source_display?: string | null;
  /** `s679` — онлайн-запись: ссылка источника, заход (канал и кампания) и проект — свод `ZapisOtkuda`. */
  onlayn_istochnik_display?: string | null;
  zahod_display?: string | null;
  proekt_display?: string | null;
  /** Первоисточники: числа, адрес кабинета собирает экран (`TZ-R-zapis` §3.1). */
  amo_lead_id?: number | null;
  yclients_record_id?: number | null;
  /** `s402` Д-28: номер кабинета вендора — из него собирается ссылка на запись. */
  yclients_company_id?: number | null;
  yclients_visit_id?: number | null;
  /** Группа «подружки»: несколько записей идут вместе (ТЗ §3.3). */
  group_id?: number | null;
  group_condition_display?: string | null;
  /** Пришла в ожидание из отмены другой записи. */
  waiting_from_booking_id?: number | null;
  /** Кто фактически был в кресле, если пришёл другой (разбор 3 §2.11). */
  attendee_display?: string | null;
  /** Длительность, поставленная рукой («тайминг час»). */
  duration_override_display?: string | null;
  note: string | null;
  legacy_kind_display: string | null;
  visit_seq_display: string | null;
  /**
   * Два остальных счёта визита тихой строкой: «в сети — 18-й, у мастера — 9-й».
   * Одно число крупно, остальные рядом объяснением (`s305`, §3.1).
   */
  visit_seq_note_display: string | null;
  days_since_prev_display: string | null;

  /** Начало отдельно от промежутка — для временно́й шкалы ленты на телефоне. */
  start_display: string | null;

  customer_id: number | null;
  customer_display: string | null;
  /** Буквы для кружка в шапке; `null`, если в имени нет ни одной буквы. */
  customer_initials?: string | null;
  filial_display: string | null;
  procedure_display: string | null;
  /**
   * Услуги записи ОТДЕЛЬНЫМИ строками (`s306`, Б-2): в журнале вендора каждая
   * стоит своей строкой. Склеенная `procedure_display` осталась для списков, где
   * на услуги отведена одна ячейка.
   */
  services?: string[];
  /**
   * Телефон клиентки КРАЯМИ (`+7 (9•• ) •••-••-72`), а не целиком — решение
   * владельца 09.09.2026, `TZ-R-zapis` §13.3. Полного номера экран не получает
   * вовсе: он раскрывается ходом через дверь `hod1`, которая пишет след.
   */
  phone_masked?: string | null;
  /** Сумма чека «к оплате» — готовой строкой для полосы плитки (`s311`). */
  check_sum_display?: string | null;
  /** Первый визит в этот филиал — бейдж «new» у имени, как у вендора. */
  is_new?: boolean;
  /**
   * Бейджи плитки: новый · повторный · предзапись (О-05, решение владельца 09.09).
   *
   * ⛔ СЧИТАЕТ СЕРВЕР. «Повторный» — это НЕ «не новый»: у записей переезда
   * порядковый номер визита бывает не заполнен вовсе, и отрицание объявило бы
   * повторными все записи, о которых мы просто ничего не знаем.
   */
  /**
   * ⛔⛔ У БЕЙДЖЕЙ ПОЛЕ ЗОВЁТСЯ `kod`, А НЕ `code`. Сервер отдаёт
   * `['kod' => …, 'title' => …]`, а тип здесь стоял `KodSlovo` с полем `code` —
   * и `b.code` был `undefined` на КАЖДОМ бейдже: класс выходил
   * `bejj-undefined`, а React-ключ у всех бейджей был один и тот же. На 5 814
   * записях бейджа два сразу, то есть ключ дублировался. Слово рисовалось
   * верно (`title`), поэтому глазами дефект не виден — найден сверкой типа с
   * ответом сервера. Тип назван своим именем, чтобы впредь не подменялся.
   */
  bejji?: BejjPlitki[];
  /** Примечание записи: у вендора стоит в скобках перед именем. */
  note_display?: string | null;
  /** Пришла из онлайн-брони: с 09.09 показывается ЗНАЧКОМ, не цветом полосы. */
  is_online?: boolean;
  check: CheckBrief | null;

  /** Чего не хватает записи, чтобы считаться закрытой (О-10). Отвечает сервер. */
  chego_net?: KodSlovo[];

  /** Желаемое окно машинными датами — для календаря-лайт (О-14). */
  wanted_from?: string | null;
  wanted_to?: string | null;

  /** Есть только в очереди Э-З4: номер отражает порядок выборки WaitingQueue. */
  queue_no?: number;

  /** Есть только у строк «Дня»: остальные экраны сетки не рисуют. */
  column_key?: string;
  top_pct?: string;
  height_pct?: string;
  left_pct?: string;
  width_pct?: string;
  lane?: number;
  lanes?: number;
  is_short?: boolean;

  /**
   * БЛОК БРОНИ ДВУХ ЛИЦ (`s329`, Д13). Какое лицо показывать, решил СЕРВЕР:
   * `vnesena` — факт, `ozhidaem_display` — обещание. Экран не складывает их сам,
   * иначе «Сумма брони» снова написалась бы там, где денег нет.
   */
  bron_zachteno_display?: string | null;
  bron?: {
    /** Unspent receipt sources after cancellation; the released hold is not a refund. */
    na_schete_summa_display?: string | null;
    refund_allowed?: boolean;
    fully_refunded?: boolean;
    vnesena: boolean;
    trebuetsya: boolean;
    /** `s722`: открыт ли ход «Внести / поправить бронь» — у отменённой записи без брони закрыт. */
    mozhno_vnesti?: boolean;
    summa_display: string | null;
    kogda_display: string | null;
    sposob_display: string | null;
    ozhidaem_display: string | null;
    srok_display: string | null;
    osvobozhdena_display: string | null;
    /** Способы — справочник касс СВОЕГО филиала (владелец: «не выдумывать»). */
    sposoby?: { id: number; title: string }[];
    /** Текущие значения — окно правки открывается заполненным. */
    summa_rubles?: number | null;
    /** Размер предоплаты филиала: подставляется в НОВУЮ бронь (владелец, 20.09). */
    summa_po_umolchaniyu_rubles?: number | null;
    data_input?: string | null;
    cash_register_id?: number | null;
  } | null;

  /** Варианты пожелания к мастеру со словами (`s329`, Д6) — словарь у сервера. */
  master_preference_varianty?: { kod: string; display: string | null }[];
  /**
   * Куда вернётся запись, лежащая на полке переносов (`s338`).
   *
   * ⛔ СЧИТАЕТ СЕРВЕР: ход `polka` зануляет время и мастера, прежнее место
   * помнит только след. `mozhno = false` — снимка в следе нет, тогда приезжает
   * `pochemu` фразой человеку.
   */
  vernut?: { mozhno: boolean; kuda_display: string | null; pochemu: string | null } | null;
}

/**
 * Место плитки в сетке дня — доли шкалы, посчитанные сервером (`s304`).
 *
 * ⛔ ПРОЦЕНТ, А НЕ ПИКСЕЛЬ, И НЕ ВРЕМЯ. Разбери экран «10:00–12:00» сам — и у
 * дня появился бы второй источник времени рядом с серверным; посчитай он
 * пиксели — высота колонки перестала бы быть вопросом вида.
 */
/**
 * Значок плитки — то, что владелец запретил показывать цветом (решение 09.09).
 *
 * `gromkiy` — «крупный и бросающийся в глаза»: так помечается визит, который пора
 * закрыть, но он не закрыт. Дословно владелец: «не пиксельная шифровка».
 */
/** Строка полки переносов: запись без времени и дело по ней. */
export interface PolkaStroka extends ZapisRow {
  polka_at_display?: string | null;
  polka_zadacha?: string | null;
}

export interface TileZnachok {
  kod: string;
  gromkiy: boolean;
  display: string | null;
}

export interface TilePlacement {
  column_key: string;
  top_pct: string;
  height_pct: string;
  left_pct: string;
  width_pct: string;
  lane: number;
  lanes: number;
  /** Запись короче 45 минут: подробности в плитку не поместятся. */
  is_short: boolean;
}

export interface StaffColumn {
  key: string;
  staff_id: number | null;
  name: string | null;
  /** `s407`: филиал колонки числом — им и заводится новая запись (находка 1). */
  filial_id: number | null;
  filial_display: string | null;
  /** Буквы для кружка колонки — запасное дно, когда фотографии нет. */
  initials: string | null;
  /** Фотография мастера из справочника вендора (`s306`, Б-3). */
  avatar_url?: string | null;
  /** Порядок колонки у вендора: больше — левее. Пусто — вендор молчит. */
  sort_weight?: number | null;
  /** Должность под именем; пустая остаётся пустой, «Мастер» не подставляется. */
  position_display: string | null;
  /** Служебная обёртка вендора «Лист ожидания», а не человек. */
  is_pseudo: boolean;
  /** `s677`: смена и свободное время — только в расписании для выбора окна. */
  smena?: SmenaKolonki;
}

export interface DayScale {
  from_display: string;
  to_display: string;
  hours_count: number;
  /** Машинное начало шкалы и её длина в минутах — для перетаскивания (`s311`). */
  from?: string;
  minutes_total?: number;
  /** Шаг шкалы — полчаса: у половины подпись «30» и свой тихий вид. */
  marks: Array<{ label: string; top_pct: string; is_half: boolean; is_first: boolean; is_last: boolean }>;
  /** Метка текущего времени: приходит ТОЛЬКО когда открыт сегодняшний день. */
  now_pct?: string;
  now_display?: string;
}

export interface StateLegend {
  state: string;
  state_display: string | null;
}

export interface StateCount {
  state: string;
  state_display: string | null;
  count: number;
}

export interface FilialOption {
  id: number;
  name: string;
}

/**
 * РЕЖИМ ПРИЁМКИ — приходит с КАЖДОГО экрана записи (`s307`, §3.1 п.3).
 *
 * ⛔ `enabled` — ПОЛЕ, А НЕ ОТСУТСТВИЕ БЛОКА. Старая сборка панели против
 * нового сервера обязана отличать «режим выключен» от «сервер промолчал»:
 * иначе баннер не нарисовался бы ровно тогда, когда он нужнее всего.
 */
export interface PriyomkaBlok {
  enabled: boolean;
  banner: string | null;
  tronuto: number;
  /** Сколько записей примера живёт сейчас (О-15): пусто — предлагаем наполнить. */
  primerov: number;
}

export interface DenScreen {
  /** Справочники фильтров журнала (V1.09/V1.12) — приезжают с экраном. */
  filters: {
    filials: FilialOption[];
    dolzhnosti: string[];
    statusy: KodSlovo[];
    vybrano: { dolzhnost: string | null; status: string | null };
  };
  /**
   * ПОЛКА ПЕРЕНОСОВ (О-07, решение владельца 09.09.2026).
   *
   * ⛔ ЕДЕТ ВМЕСТЕ С ДНЁМ, А НЕ СВОИМ ЗАПРОСОМ: панель стоит прямо на журнале,
   * администратор перетаскивает в неё плитку и тут же видит, что там лежит.
   */
  /**
   * ⛔ `total` — ВСЯ полка, `pokazano` — сколько приехало (`s316`, Y31).
   *
   * ⛔ СЧЁТ ВЫДАЧИ — ОБЩИЙ, КАК У ВСЕХ СПИСКОВ (`s319`, З-09). Был свой
   * `est_eshchyo?: boolean` вместо `Stranica`: экран не знал признака «упёрлись
   * в потолок», и на двухсотой строке кнопка «Показать ещё» жалась вечно
   * впустую. Теперь полка судится тем же `Eshchyo`, что и остальные списки.
   */
  polka: { rows: PolkaStroka[]; total: number; pokazano: number } & Stranica;
  date: string;
  date_display: string | null;
  /** Дата словами для шапки: «вторник, 8 сентября 2026». */
  date_full_display: string | null;
  /** Короткая дата для узкой шапки телефона (`s311`). */
  date_short_display?: string | null;
  /** День словами без дня недели для шапки журнала: «20 сентября 2026» (`s321`). */
  /** ⛔ `s329` Д26: «18 сентября, пятница» — шапка журнала без года. */
  date_bez_goda_display?: string | null;
  /** ⛔ `s329` Д11: «19.09» — компактная дата для строки телефона. */
  date_kratko_display?: string | null;
  date_slovami_display?: string | null;
  filial_id: number | null;
  /** Имя филиала для шапки — расписание живёт в одном филиале. */
  filial_display: string;
  columns: Column[];
  scale: DayScale;
  staff_columns: StaffColumn[];
  /** Условные обозначения сетки: слова состояний приходят из базы. */
  legend: StateLegend[];
  rows: ZapisRow[];
  counts: {
    total: number;
    total_display: string;
    by_state: StateCount[];
    /** Разбор 3 §6: «18 записей · 3 без брони · 1 ждёт окна». Ноль не показывается. */
    /** Закрытые визиты дня: «закрыт» ушёл из цвета, но не из счёта смены. */
    zakryto: number;
    /** Пришла, слот кончился, визит не закрыт — громкий значок на плитке. */
    ne_zakryto: number;
    /** Полка переносов: сколько записей лежит без нового времени (О-07). */
    na_polke: number;
    na_polke_display: string | null;
    bez_broni: number;
    bez_broni_display: string | null;
    zhdut_okna: number;
    zhdut_okna_display: string | null;
    /** Г-1 (`s406`): сумма дня готовой подписью с сервера. Пусто — цен нет вовсе. */
    summa_dnya_display?: string | null;
    /** Сколько записей вошло в сумму и сколько осталось без цены — рядом, честно. */
    summa_dnya_uchteno?: number;
    summa_dnya_bez_ceny?: number;
    /**
     * Вторая сумма дня — «Выполнено» (Д2, владелец: «сумма всех записей и
     * отдельно сумма уже выполненных»). Считает сервер: пришла или закрыт.
     * Своей арифметики на экране не заводим.
     */
    summa_vypolneno_display?: string | null;
    summa_vypolneno_shtuk?: number;
  };
  empty_caption: string | null;
  hints: Hints;
  words: ScreenWords;
  priyomka?: PriyomkaBlok;
}

export interface DenQuery {
  /** Фильтры журнала вендора: «Все должности» (V1.09) и статус визита (V1.12). */
  dolzhnost?: string | null;
  status?: string | null;
  date?: string;
  filial_id?: number | null;
  /** Окно полки переносов: «Показать ещё» (`s316`, `kau1` Y31). */
  polka_pokazat?: number | null;
  /** `s677`: расписание для выбора окна — мастера смены и их свободное время. */
  smeny?: boolean;
}

export async function fetchDen(query: DenQuery): Promise<DenScreen> {
  const { data } = await apiClient.get<DenScreen>('/zapis/den', {
    params: {
      date: query.date,
      filial_id: query.filial_id ?? undefined,
      dolzhnost: query.dolzhnost || undefined,
      status: query.status || undefined,
      polka_pokazat: query.polka_pokazat ?? undefined,
      smeny: query.smeny ? 1 : undefined,
    },
  });

  return data;
}

/**
 * ОТРЕЗОК НА ШКАЛЕ ДНЯ (`s677`) — смена мастера или свободное время в ней.
 * ⛔ Доли шкалы считает сервер, как у плитки: экран не складывает минут.
 */
export interface OtrezokShkaly {
  s: string;
  do: string;
  /** Машинное начало «ГГГГ-ММ-ДД ЧЧ:ММ:00» — им заводится запись по щелчку. */
  nachalo: string;
  top_pct: string;
  height_pct: string;
  minut: number;
}

/** Смена колонки по плану; `izvesten=false` — план не составлен, свободно по часам филиала. */
export interface SmenaKolonki {
  izvesten: boolean;
  rabotaet: boolean;
  otrezki: OtrezokShkaly[];
  svobodno: OtrezokShkaly[];
}

/** «Ближайшее свободное» (`s677`): день, мастер и начало первого окна. */
export async function fetchBlizhayshee(q: { filial_id: number | null; ot?: string; mastera?: number[] }): Promise<{ date: string; staff_id: number; nachalo: string } | null> {
  /* ⛔ Филиал — тот, что рисует панель (шапка или филиал переписки), а не
     сохранённый у учётки: иначе окно искалось бы в чужом филиале (поймано кадром). */
  const { data } = await apiClient.get<{ okno: { date: string; staff_id: number; nachalo: string } | null }>('/zapis/blizhayshee', {
    params: { filial_id: q.filial_id ?? undefined, ot: q.ot, 'mastera[]': q.mastera && q.mastera.length > 0 ? q.mastera : undefined },
  });

  return data.okno;
}

/** Встаёт ли визит услуги в окно мастера (`s677`) — ответ того же правила, что держит дверь записи. */
export interface OtvetOkna {
  konec: string;
  vremya_display: string;
  minut: number;
  vstayot: boolean;
  pochemu: string | null;
}

export async function fetchOkno(q: { filial_id: number; staff_id: number; slot_start: string; procedure_id?: number | null }): Promise<OtvetOkna> {
  const { data } = await apiClient.get<OtvetOkna>('/zapis/okno', {
    params: { ...q, procedure_id: q.procedure_id ?? undefined },
  });

  return data;
}

/** Услуги новой записи (`s677`) — тот же справочник, что у карточки, по филиалу и мастеру. */
export async function fetchUslugiNovoy(filialId: number, staffId: number | null, q: string): Promise<{ rows: UslugaSpravochnika[] }> {
  const { data } = await apiClient.get<{ rows: UslugaSpravochnika[] }>('/zapis/novaya-uslugi', {
    params: { filial_id: filialId, staff_id: staffId ?? undefined, q },
  });

  return data;
}

// ── Э-З2 «Запись» ────────────────────────────────────────────────────────────

/** Бейдж плитки: новая · повторный · предзапись. Поле кода — `kod` (как у сервера). */
export interface BejjPlitki {
  kod: string;
  title: string;
}

/** Пара «код — слово»: код участвует в выборке, слово приезжает из базы. */
export interface KodSlovo {
  code: string;
  title: string;
}

export interface Reason {
  id: number;
  /** `s407`: «Другое» узнаётся по коду, а не по заголовку — заголовок правят. */
  code: string | null;
  title: string;
  /** Сторона причины (`client`/`salon`/`robot`) — по ней предвыбирается «кто попросил». */
  side: string | null;
  side_display: string | null;
  initiated_by_default: string | null;
  reschedule_intent: boolean;
}

/**
 * Точка действия в координатах окна — куда встаёт отказ (`s322`, Д41).
 *
 * ⛔ ЕЁ НАЗЫВАЕТ ТОТ, КТО ЗВАЛ ХОД, А НЕ УГАДЫВАЕТ ПОЛУЧАТЕЛЬ. Отказ «в месте
 * действия» без точки действия — это тот же баннер сверху.
 */
export interface Tochka {
  x: number;
  y: number;
}

/**
 * Справочник причин одного рода — своим запросом (`s322`, Д54).
 *
 * ⛔ ЖУРНАЛ ТЯНЕТ ЕГО ТОЛЬКО ПРИ ОТКРЫТИИ ОКНА ПРИЧИНЫ. Пристегни справочник к
 * ответу дня — и десять его строк ездили бы на каждую перерисовку сетки ради
 * жеста, который делают несколько раз за день.
 */
export async function fetchPrichiny(
  kind: 'cancel' | 'transfer' | 'no_show' | 'reopen' | 'restore' | 'defer',
  transferContext?: 'drugoy_master' | 'drugaya_data',
): Promise<Reason[]> {
  const { data } = await apiClient.get<{ rows: Reason[] }>('/zapis/prichiny', { params: { kind, ...(transferContext ? { transfer_context: transferContext } : {}) } });

  return data.rows;
}

/**
 * ЗАНЯТОСТЬ МАСТЕРА НА ДЕНЬ (`s322`, Д25, первая половина).
 *
 * ⛔ ПОЛОВИНА, И ОНА НАЗВАНА ПОЛЕМ. `grafik_izvesten` сегодня всегда `false`:
 * графика смен в системе нет вовсе (этап «Мастер + смена», `TZ-R-grafik`).
 * «Свободно» поэтому значит «нет другой записи», а не «мастер работает» — и
 * подпись над сеткой остаётся «Время», а не «Свободное время» (строка В-14).
 */
export interface ZanyatostDnya {
  /**
   * s966: `pochemu` — слово сервера, почему окно серое (прошлое, вне смены, выходной,
   * не успевает до конца смены, занято записью). Без слова окно молчит: общее
   * «здесь уже есть запись» на пустом часе было неправдой.
   */
  rows: Array<{ vremya: string; svobodno: boolean; pochemu?: string | null }>;
  s: string;
  do: string;
  grafik_izvesten: boolean;
}

export async function fetchZanyatost(q: {
  date: string;
  staff_id?: number | null;
  minut?: number | null;
  krome?: number | null;
}): Promise<ZanyatostDnya> {
  const { data } = await apiClient.get<ZanyatostDnya>('/zapis/zanyatost', {
    params: {
      date: q.date,
      staff_id: q.staff_id ?? undefined,
      minut: q.minut ?? undefined,
      krome: q.krome ?? undefined,
    },
  });

  return data;
}

export interface ZapisReasons {
  cancel: Reason[];
  transfer: Reason[];
  no_show: Reason[];
  /** `s407`, находка 9: у переоткрытия и возврата теперь свой справочник. */
  reopen: Reason[];
  restore: Reason[];
}

export interface StaffOption {
  id: number;
  name: string;
  filial_id: number | null;
  filial_display: string | null;
}

/** «По чьему решению» — род решения словами из базы, не имя человека. */
export interface Initiator {
  code: string;
  title: string;
}

export interface ZapisScreen {
  access?: Partial<import('./cardPermissions').BookingCardAccess>;
  booking: ZapisRow;
  check: CheckBrief | null;
  /** Живой состав позициями: услуги, товары и «К оплате» одним числом (`s311`). */
  sostav: SostavZapisi;
  /** `s401`: на что записана, норма, расхождение плана и факта. */
  plan?: PlanVizita;
  reasons: ZapisReasons;
  /** Кто мог решить: род решения, а не имя филиала или мастера (`s305`). */
  initiators: Initiator[];
  /** Справочники формы переноса — едут вместе с записью, не отдельным запросом. */
  directories: { staff: StaffOption[]; filials: FilialOption[] };
  /**
   * Ходы машины по текущему состоянию: что живо, что нет и почему.
   *
   * ⛔ ЭКРАН НЕ РЕШАЕТ ЭТОТ ВОПРОС САМ. Единственная таблица допустимости живёт
   * в машине (`BookingTransition::IZ_SOSTOYANIY`); свой список в JSX однажды
   * показал бы кнопку, которую машина отвергнет (`notes.md` Э-З2).
   */
  perehody: Perehod[];
  /** Последние ходы R1 над этой записью — не полная история правок карточки. */
  sled: SledStroka[];
  /**
   * Каналы подтверждения — ступени 2 и 3 шкалы статуса (§13.1).
   *
   * ⛔ СПИСОК ЕДЕТ С СЕРВЕРА, А НЕ ЛЕЖИТ ДВУМЯ КНОПКАМИ В JSX. Из этого поля
   * читается ступень и цвет плитки: подставь канал жёстко — и запись, где
   * клиентка нажала кнопку в рассылке, покрасится «подтверждена звонком».
   */
  kanaly_podtverzhdeniya?: KanalPodtverzhdeniya[];
  hints: Hints;
  words: ScreenWords;
  priyomka?: PriyomkaBlok;
  /** Отказы, снятые режимом приёмки последним ходом — жёлтый аллерт. */
  priyomka_snyato?: Array<{ pravilo: string; text: string }>;
}

export interface Perehod {
  hod: string;
  label: string;
  mozhno: boolean;
  /** Ход доступен только потому, что включён режим приёмки. */
  v_boyu_nelzya: boolean;
  pochemu: string | null;
  /**
   * Г-5 (`s406`): ход ведёт туда, где запись УЖЕ стоит, — и потому называется
   * действием, а не состоянием («Отметить подтверждение» вместо «Подтвердила»).
   * Решает сервер: граф ходов живёт в его таблице, а не здесь.
   */
  povtor?: boolean;
  /**
   * ⛔ ХОД СТОИТ В ПОЛОСЕ СОСТОЯНИЙ (`s341`, Р2). Считает свод
   * `PolosaSostoyaniy` на сервере: ходы вперёд из ТЕКУЩЕГО состояния плюс
   * ровно один откат назад. Это НЕ то же самое, что `mozhno`: машина
   * допускает больше, чем уместно предложить первым рядом (`no_show` из
   * `arrived` — исправление ошибки, а не предложение). Ход с `false`
   * не пропадает — он уезжает в «редкие».
   */
  v_polose?: boolean;
  /** Место хода внутри полосы: вперёд по порядку, откат последним. */
  polosa_nomer?: number | null;
}

export interface SledStroka {
  action: string;
  action_display: string;
  actor_display: string | null;
  reason: string | null;
  at_display: string | null;
}

/** Канал подтверждения — он же СТУПЕНЬ шкалы статуса (§13.1). */
export interface KanalPodtverzhdeniya {
  kod: string;
  display: string | null;
  stupen: string;
}

/**
 * НОВАЯ ЗАПИСЬ С НУЛЯ (`s402`, Д-23 приёмки владельца 13.09.2026).
 *
 * ⛔ ОКОНЧАНИЕ СЧИТАЕТ СЕРВЕР ПО НОРМЕ ПРОЦЕДУРЫ. Здесь его не шлём вовсе,
 * когда назвали процедуру: вторая арифметика длительности в браузере разошлась
 * бы с той, что уже стоит в «На что записана».
 */
export interface NovayaZapis {
  filial_id: number;
  staff_id: number;
  customer_id: number;
  slot_start: string;
  slot_end?: string;
  procedure_id?: number;
  note?: string;
}

export async function sozdatZapis(telo: NovayaZapis): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>('/zapis', telo);
  return data;
}

export async function fetchZapis(id: number): Promise<ZapisScreen> {
  const { data } = await apiClient.get<ZapisScreen>(`/zapis/${id}`);
  return data;
}


// ── Два окна вендора: истории (решение владельца 09.09.2026, О-20/О-21) ──────

/**
 * ⛔ У ЭТИХ ОКОН СВОИ АДРЕСА, А НЕ ПОЛЯ В ОТВЕТЕ КАРТОЧКИ. История посещений
 * бывает длиной в годы, история изменений — в десятки ходов; вложи их в
 * карточку — и она тяжелела бы от того, чего никто не открыл.
 */
export type SostavRod = 'service' | 'good';

/** Позиция живого состава записи: свой номер, своя цена, свой род. */
/**
 * Правка строки состава (`s404` Д-34, `s320` Д22/Д57).
 *
 * ⛔ Скидка называется ЛИБО рублями, ЛИБО процентом — сервер отвергает оба
 * поля разом: две единицы одной величины в одном запросе означают, что экран
 * сам не знает, что отправил.
 */
export interface SostavPravka {
  drug_cost_rubles?: string | null;
  amount?: number;
  /** `s333`: цена строки — в рублях, как её набирает человек; копейки делает сервер. */
  cena_rubles?: number;
  discount_rubles?: number;
  discount_percent?: number;
  /** `s320` Д57: заменить процедуру, сохранив строку — отсюда «было → стало». */
  procedure_id?: number;
  /**
   * ⛔ ПОЯСНЕНИЕ «ПОЧЕМУ ДЕШЕВЛЕ» — ТОЙ ЖЕ ДВЕРЬЮ, ЧТО И ЦЕНА (владелец,
   * 19.09.2026: «в САМОЙ позиции появляется строка „Почему дешевле" с полем
   * ввода, там же, где цену и правили»). Своя дверь к той же строке чека
   * разошлась бы с этой в правах и в следе.
   */
  downgrade_note?: string;
}

export interface SostavPoziciya {
  id: number;
  rod: SostavRod;
  /**
   * Имя КЛИЕНТСКОЕ, из каталога (`s402`, Д-19). Стрелочная разметка вендора
   * («↗», «↑↑») на экран больше не выходит — она осталась в следе и в
   * `procedure_aliases`, где ей и место.
   */
  title: string;
  /** Имя пришло из каталога, а не текстом вендора. Отвечает сервер, не экран. */
  iz_kataloga?: boolean;
  /** ⛔ `s329` Д29: номер процедуры строки — по нему замена сужается до её категории. */
  procedure_id?: number | null;
  amount: number;
  /** V2.19: мастер и норма — серой строкой под названием. */
  master_display: string | null;
  dlitelnost_display: string | null;
  /** Цена до скидки; V2.22 — скидка построчно, `null` значит «не давали». */
  cena_display: string | null;
  /** `s333`: цена за единицу в рублях — для поля правки, готовым числом с сервера. */
  cena_rubli?: number;
  skidka_display: string | null;
  /** `s404`, Д-34: величина для поля правки — в рублях, готовой с сервера. */
  skidka_rubli?: number;
  /**
   * ⛔ ЦЕНА ОПУЩЕНА НИЖЕ ПРЕЙСКУРАНТА (владелец, 19.09.2026). Признак считает
   * СТРОКА ЧЕКА (`CheckServiceLine::isDowngrade`), а не экран сравнением двух
   * чисел: правило живёт там же, где гейт закрытия.
   */
  deshevle?: boolean;
  /** Прейскурантная цена — чтобы строка сказала, ОТ ЧЕГО дешевле. */
  prays_display?: string | null;
  /** Уже написанное пояснение — поле открывается заполненным. */
  pochemu_deshevle?: string | null;
  /**
   * `s320`, Д22: та же скидка процентом — тоже готовым числом с сервера.
   * Считать её в браузере значило бы завести вторую арифметику одной величины.
   */
  skidka_procent?: number;
  /**
   * `s320`, Д57: на что записывались, если процедуру строки заменили.
   * `null` у строки, которую не меняли: «Лифтинг → Лифтинг» — не новость.
   */
  bylo_display?: string | null;
  cost_kopeks: number;
  cost_display: string | null;
  /** Позиция приехала из зеркала вендора: показать можно, править — нет. */
  iz_zerkala?: boolean;
  /**
   * ⛔⛔ `s336` (слово владельца 19.09.2026): КТО ПРОДАЛ — в самой позиции.
   * Это НЕ исполнитель услуги (`master_display`) и НЕ длительность: обоих
   * владелец из раскрытой позиции убрал, поле другое.
   */
  prodavec_display?: string;
  /** s966: все продавцы позиции словами — «Записал: Наталья · админ», «Поднял до дороже: София · мастер». */
  prodavcy_cepochka?: string[];
  /** Кто продал итогом: у апгрейда — тот, кто поднял. */
  prodavec_itog_display?: string;
  /** Кто записал то, что было до апгрейда: «Наталья · админ». */
  bylo_kto_display?: string | null;
  /** Происхождение значения словами: «предложено системой · подтвердил X · изменил X». */
  prodavec_otkuda?: string;
  /** Почему выбран именно он — фраза пункта правила. Пусто у старых событий. */
  prodavec_pochemu?: string | null;
  /** Спросить ли человека. Решает ПРАВИЛО на сервере, а не экран. */
  prodavec_trebuet?: boolean;
  prodavec_rod?: ProdavecRod;
  prodavec_staff_id?: number | null;
  prodavec_user_id?: number | null;
}

/** Кем бывает продавец позиции (`s336`). «Никому» — законный исход правила, п.5. */
export type ProdavecRod = 'staff' | 'user' | 'nikomu';

/** Из кого выбирать продавца — список собирает сервер, не экран. */
export interface KandidatVProdavcy {
  rod: ProdavecRod;
  id: number | null;
  imya: string;
}

/** Событие продажи по позиции чека (`s402`, Ф6; разбор 1 §2.3–2.4). */
export interface SobytieProdazhi {
  id: number;
  poziciya_id: number | null;
  /** Товар с полки: у него свой продавец и нет фазы визита (`s403`). */
  tovar_id: number | null;
  kind_display: string;
  rol_display: string;
  /** Пусто у клиентки и робота — «никому не засчитывается». */
  kto_display: string | null;
  faza_display: string;
  summa_display: string;
  kross: boolean;
  podtverzhdeno: boolean;
  /**
   * `s403`: событие восстановлено проходом по истории — вычислено по журналу
   * кабинета вендора задним числом, а не записано рядом с человеком у кнопки.
   * Показывается вслух: догадка не должна читаться так же уверенно, как факт.
   */
  vosstanovleno: boolean;
  /** Кто внёс правку в кабинете вендора — имя и роль; закон судит по фазе. */
  vnes_display: string | null;
  at_display: string | null;
  /** `s336`: «предложено системой · подтвердил X · изменил X» — словами. */
  otkuda_display?: string;
  /** `s336`: почему выбран именно он. Пусто у событий исторического разбора. */
  pochemu_display?: string | null;
  /** `s336`: продавца услуги можно поставить рукой; у товара он факт вендора. */
  mozhno_smenit?: boolean;
}

export interface SostavZapisi {
  uslugi: SostavPoziciya[];
  tovary: SostavPoziciya[];
  k_oplate_display: string;
  /** `s407`: сумма известна — решает сервер, а не сравнение с «0 ₽» на экране. */
  k_oplate_est?: boolean;
  /**
   * `s409` Н1: итог ВИДИМОЙ закладки. Строка «Итого» стоит под колонкой «Сумма»
   * и обязана отвечать за то, что в этой колонке видно, — а не за весь чек.
   */
  itogo_uslugi_display?: string;
  itogo_tovary_display?: string;
  /** Закладок больше одной — общий итог нужен отдельной строкой. Решает сервер. */
  vsego_po_cheku_nuzhno?: boolean;
  /** Закрытый визит состав не правит — экран говорит это ДО нажатия. */
  pravitsya: boolean;
  /** Состав показан из зеркала вендора — у позиций нет ни номера, ни цены. */
  iz_zerkala?: boolean;
  /**
   * ⛔ `s336`: номер позиции, о продавце которой спросить ПРЯМО СЕЙЧАС.
   * Называет сервер: после добавления экран не знает номера новой строки, а
   * искать её «последней по номеру» значило бы гадать.
   */
  sprosit_prodavca?: number | null;
  prodavcy_spisok?: KandidatVProdavcy[];
  /**
   * ⛔ `s336`, приёмка 20.09: номера позиций, которые ДЕРЖАТ сохранение записи
   * и закрытие визита — у них продавец ещё не подтверждён и не поставлен рукой.
   * Считает ГЕЙТ на сервере: второй ответ на «какие строки ждут» разошёлся бы
   * с текстом отказа.
   */
  zhdut_prodavca?: number[];
}

export interface UslugaSpravochnika {
  id: number;
  title: string;
  cost_kopeks: number;
  cost_display: string;
  /**
   * `s401`: норма длительности процедуры. `null` — «норма не задана» честно (у 40
   * процедур её нет), а не «нисколько». Товарный справочник её не присылает.
   */
  norma_minut?: number | null;
  norma_display?: string;
  /** `s402` Ф7: раздел дерева каталога — экран только группирует по нему. */
  razdel_display?: string | null;
  /** ⛔ `s329` Д18: номер раздела — по нему экран собирает дерево, не склеивая имена. */
  razdel_id?: number | null;
  /** ⛔ `s329` Д18: у услуги не задана норма длительности — видна, но помечена. */
  bez_normy?: boolean;
}

/** `s401`: план визита — на что клиентка записана и что из этого следует. */
export interface PlanVizita {
  est: boolean;
  /** `s404`, Д-34: имя взято из состава визита, а не из плана записи. */
  po_sostavu?: boolean;
  procedure_id: number | null;
  display: string;
  norma_display: string | null;
  okonchanie_po_norme: string | null;
  okonchanie_po_norme_display: string | null;
  rashozhdenie: string | null;
  sdelano?: {
    est_chek: boolean;
    sovpalo: boolean | null;
    display: string;
  };
}

export interface SostavDobavka {
  kind: SostavRod;
  title: string;
  procedure_id?: number | null;
  cost_to_pay_kopeks: number;
}

/**
 * ПЕРЕНОС ПЛИТКИ ПО СЕТКЕ (`s311`, владелец 09.09.2026).
 *
 * ⛔ ДВА РАЗНЫХ ХОДА ПОД ОДНИМ ЖЕСТОМ. Бросок в колонку мастера — перенос
 * (`move`): время и исполнитель. Бросок в «Лист ожидания» — уход в ожидание окна
 * (`park`): времени у записи не остаётся вовсе. Слей их в один ход — и запись,
 * брошенная в ожидание, осталась бы стоять на прежнем часе.
 */
export interface PerenosTelo {
  slot_start?: string;
  slot_end?: string;
  staff_id?: number | null;
  v_ozhidanie?: boolean;
  /**
   * Причина переноса — спрашивается ПОСЛЕ отпускания плитки (`s322`, Д54).
   *
   * ⛔ ОБЯЗАТЕЛЬНА РОВНО ТАМ, ГДЕ ЕЁ ТРЕБУЕТ МАШИНА: при смене мастера. Правило
   * живёт в `BookingTransition` (ход `move`) — здесь оно только передаётся.
   */
  reason_id?: number;
  /** Слова человека к причине — необязательны (op-rules §31.9). */
  note?: string;
  /** Плитку вернули С ПОЛКИ: это «поставить на время», а не перенос. */
  s_polki?: boolean;
  /**
   * ⛔ КТО ПОПРОСИЛ ПЕРЕНЕСТИ (владелец, 19.09.2026): по нему растёт счётчик
   * «переносы по вине клиента». Не назвали — сервер НЕ УГАДЫВАЕТ, счётчик
   * стоит. Выбранная причина свою сторону приносит сама.
   */
  initiated_by?: 'client' | 'salon';
}

export interface PredzapisTelo {
  slot_start: string;
  /** `s402` Ф3: не назвали — сервер посчитает по норме выбранной процедуры. */
  slot_end?: string;
  staff_id?: number | null;
  /** `s402` Ф3: на что записываем — из каталога, как в обычной записи. */
  procedure_id?: number;
  /** Все выбранные процедуры в порядке выбора; сервер считает общую длительность. */
  procedure_ids?: number[];
}

/** Н-06: пожелания «хочу раньше» — хотя бы одно, иначе машина отказывает (И-З2). */
export interface HochuRansheTelo {
  wanted_from?: string | null;
  wanted_to?: string | null;
  wanted_time_of_day?: 'morning' | 'day' | 'evening' | 'any' | null;
  /** Точное время «ЧЧ:ММ» (`s328`, Ф3) — уточнение сверх части дня, не замена. */
  wanted_time?: string | null;
  wanted_staff_id?: number | null;
}

/** Н-28: отсрочка брони — ДАТА и причина, обе обязательны. */
export interface OtsrochkaTelo {
  due_at: string;
  /**
   * ⛔ ПРИЧИНА — ИЗ СПРАВОЧНИКА (`s322`, Д61, §31.9): обязателен ВЫБОР, слова —
   * по желанию. Старый вход `reason` словами остаётся живым: на сервере без
   * миграции справочник пуст, и отобрать единственный вход значило бы сломать
   * отсрочку выкаткой панели вперёд сервера.
   */
  reason_id?: number;
  reason?: string;
}

export interface IzmenenieDelta {
  pole: string;
  pole_display: string | null;
  bylo: string | null;
  stalo: string | null;
  /** `s715`: одно значение события (срок, сумма), а не перемена — «было» у него нет. */
  odno?: boolean;
}

/** `s715`: кто сделал — сотрудник, бот, источник, автоматика или клиентка; имя — только записанное. */
export interface IzmenenieKto {
  vid: 'sotrudnik' | 'bot' | 'istochnik' | 'avtomatika' | 'klientka';
  podpis: string;
  imya: string | null;
  /** Честная приписка: «кто именно внёс в УС — не записано», «имя не записано». */
  utochnenie: string | null;
}

export interface IzmenenieRow {
  action: string;
  action_display: string;
  actor_display: string | null;
  at_display: string | null;
  reason: string | null;
  /** Создание записи показывается сводкой: «было» у него нет. */
  svodka: boolean;
  delty: IzmenenieDelta[];
  /** Честная приписка, когда источник не отдал подробностей правки (`s311`). */
  note: string | null;
  /** Откуда строка: след нашей машины или история вендора до переезда. */
  istochnik: 'nash' | 'vendor';
  /* `s715`: поля ниже прежний сервер не шлёт — окно без них рисует строку по-старому. */
  kto?: IzmenenieKto | null;
  /** Событие, после которого запись сменила этап доски. */
  povod?: string | null;
  /** Чем кончилось: ход бота или срабатывание правила — только записанное. */
  rezultat?: string[];
  /** Коды, номера и служебные метки — раскрываются по нажатию. */
  podrobnosti?: Array<{ podpis: string; znachenie: string }>;
}

export interface IstoriyaIzmeneniy {
  rows: IzmenenieRow[];
  total: number;
  total_display: string;
  /** `s715`: подписи строк окна — едут вместе с данными. */
  slova?: { rezultat: string; povod: string; podrobnosti: string };
}

export interface PoseshchenieRow {
  id: number;
  staff_display: string | null;
  staff_avatar: string | null;
  staff_initials: string | null;
  time_display: string | null;
  status_vida: string;
  status_vida_display: string | null;
  znachki: TileZnachok[];
  services: string[];
  goods: string[];
  gross_display: string | null;
  discount_display: string | null;
  /** `s342`: скидка процентом для бейджа («-10%»). */
  discount_procent_display?: string | null;
  k_oplate_display: string | null;
  paid_display: string | null;
  /** Недоплата — красным; пусто значит «сошлось», а не «ноль». */
  dolg_display: string | null;
  comment: string | null;
  mozhno_povtorit: boolean;
}

export interface PoseshchenieGroup {
  date: string;
  /** Филиал стоит у ДАТЫ, а не у каждой строки (П-08). */
  filial_display: string | null;
  date_display: string | null;
  rows: PoseshchenieRow[];
}

export interface IstoriyaPoseshcheniy {
  page?: number;
  more?: boolean;
  rows: PoseshchenieGroup[];
  filtr: string;
  total: number;
  total_display: string;
}

export async function fetchIzmeneniya(bookingId: number): Promise<IstoriyaIzmeneniy> {
  const { data } = await apiClient.get<IstoriyaIzmeneniy>(`/zapis/${bookingId}/izmeneniya`);
  return data;
}

export async function fetchPoseshcheniya(customerId: number, filtr: 'all' | 'visits' | 'no_show', page=1): Promise<IstoriyaPoseshcheniy> {
  const { data } = await apiClient.get<IstoriyaPoseshcheniy>(`/zapis/poseshcheniya/${customerId}`, {
    params: { filtr, page },
  });
  return data;
}

// ── Окно чека из списка «Чек без записи» (О-16, решение владельца 09.09) ─────

/**
 * ⛔ ОКНО, А НЕ СТРОКА СПИСКА. В список влезает «сколько», в окно — «кому, что
 * построчно, кто продал и как оплатили». Владелец назвал состав дословно.
 */
export interface ChekPoziciya {
  id: number;
  rod: 'service' | 'good';
  rod_display: string | null;
  title: string;
  amount: number;
  first_cost_display: string | null;
  /** Скидка — бейджем; пусто значит «без скидки», а не «ноль». */
  discount_display: string | null;
  cost_display: string | null;
  seller_display: string | null;
  chain_confirmed: boolean;
}

export interface ChekOkno {
  check: CheckFull & {
    filial_display?: string | null;
    customer_id?: number | null;
    customer_display?: string | null;
  };
  services: ChekPoziciya[];
  goods: ChekPoziciya[];
  documents: { id: number; total_display: string | null; at_display: string | null }[];
  hints: Hints;
  words: ScreenWords;
}

export async function fetchChek(checkId: number): Promise<ChekOkno> {
  const { data } = await apiClient.get<ChekOkno>(`/zapis/chek/${checkId}`);
  return data;
}

// ── Э-З3 «Закрытие визита» ───────────────────────────────────────────────────


export interface CheckLine {
  drug_cost_required?: boolean;
  drug_cost_editable?: boolean;
  drug_cost_rubli?: string | null;
  id: number;
  kind: 'service' | 'good';
  kind_display: string | null;
  title: string;
  amount: number;
  sum_display: string;
  /** `s342`: цена за единицу и скидка строки — тем же сборщиком, что в карточке. */
  cena_display?: string | null;
  skidka_display?: string | null;
  sale_chain_confirmed: boolean;
  /**
   * `s403`: отметка цепочки стоит, и её поставил ЧЕЛОВЕК. Переезд зеркала
   * проставил отметку 78 466 строкам из 78 467, не назвав никого, — зелёное
   * «Подтверждено» по такой отметке говорило бы, что продавца проверили.
   */
  chain_confirmed_by_human?: boolean;
  is_downgrade: boolean;
  downgrade_note: string | null;
  /**
   * `s966` D5: продавец ТОВАРА для блока «Кто продал» — мастер строки, а не
   * записан — мастер визита. Имя называет сервер; у услуги полей нет.
   */
  prodavec_staff_id?: number | null;
  /** Продавец товара — администратор (учётка); тогда `prodavec_staff_id` пуст. */
  prodavec_rod?: 'staff' | 'user' | 'nikomu';
  prodavec_user_id?: number | null;
  prodavec_display?: string | null;
  /** s966: у услуги — тот же итог продавца, что в карточке; у апгрейда — кто поднял. */
  prodavec_itog_display?: string | null;
  /** Что было до замены и кто это записал — третья строка позиции. */
  bylo_display?: string | null;
  bylo_kto_display?: string | null;
  mozhno_smenit_prodavca?: boolean;
}

export interface CheckFull {
  amount_known?: boolean;
  id: number;
  /** Кому продали — у розничного чека может не быть вовсе («без карточки»). */
  customer_id?: number | null;
  customer_display?: string | null;
  /** Что продали — короткой строкой; полный состав живёт в окне чека (О-17). */
  pozicii_display?: string | null;
  kind: string;
  kind_display: string | null;
  status_display: string | null;
  payment_state: string | null;
  payment_state_display: string | null;
  is_paid: boolean;
  gross_display: string;
  discount_display: string | null;
  k_oplate_display: string;
  goods_display: string | null;
  paid_display: string;
  dolg_display: string | null;
  opened_at_display: string | null;
  closed_at_display: string | null;
}

/** Н-11: бронь в окне закрытия — суммой словами, считает расчётный слой. */
export interface BronZakrytiya {
  hold_display: string;
  already_paid_display?: string | null;
  zachest_display: string;
  ostatok_display: string | null;
}

/**
 * ПРИЁМ ДЕНЕГ В ОКНЕ ЗАКРЫТИЯ (`s521`, деньги-минимум ко дню X).
 *
 * ⛔ ВСЕ ВЕЛИЧИНЫ ПРИХОДЯТ ГОТОВЫМИ СТРОКАМИ. «Осталось оплатить» считает сервер
 * тем же кодом, который потом и принимает деньги; своя арифметика на экране
 * стала бы вторым ответом на один вопрос и разошлась бы с машиной.
 *
 * ⛔ `mozhno_prinyat` / `mozhno_stornirovat` — НЕ ЗАМОК, а «показывать ли кнопку».
 * Настоящий замок стоит на маршрутах (`check.payment.accept` / `.storno`).
 */
export interface OplataZakrytiya {
  rollover_due_kopeks?: number;
  rollover_due_display?: string;
  can_accept_payment?: boolean;
  amount_known?: boolean;
  cash_tails?: boolean;
  /**
   * ⛔ «Сколько можно принять» — НЕ «сколько осталось получить».
   *
   * Приняли 2 000 наличными и 3 990 переводом: принимать больше нечего, а
   * получено пока 2 000 — перевод ждёт подтверждения. Две разные величины, и
   * подписаны они разными словами (найдено глазами замером боя `s521`).
   */
  mozhno_prinyat_kopeks: number;
  mozhno_prinyat_display: string;
  /** Есть принятые переводы, чей приход ещё не подтверждён. */
  zhdyom_prihoda: boolean;
  mozhno_prinyat: boolean;
  /** Подтвердить приход — не то же, что принять деньги: своё условие показа. */
  mozhno_podtverzhdat: boolean;
  mozhno_stornirovat: boolean;
  sposoby: Array<{
    id: number;
    title: string;
    po_umolchaniyu: boolean;
    /** Перевод и СБП: деньги приходят позже, приход подтверждает человек рукой. */
    trebuet_podtverzhdeniya: boolean;
    is_cash?: boolean;
  }>;
  prinyatye: Array<{
    id: number;
    can_reverse?: boolean;
    summa_display: string;
    kogda_display: string | null;
    sposob_display: string | null;
    kto_display: string | null;
    proverka_display: string | null;
    zhdyot_podtverzhdeniya: boolean;
    surplus_display?: string | null;
  }>;
}

/**
 * СВОД ГЕЙТА ЗАКРЫТИЯ — одна форма на экран закрытия И на отказ хода
 * (process-rules §38, `s337`). Экран ПОСЛЕ нажатия не вправе знать меньше, чем
 * экран до нажатия.
 */
export interface ZakrytieGate {
  can_close?: boolean;
  ne_gotovo?: number;
  kody?: string[];
  conditions: Array<{
    code: string;
    passed: boolean;
    text: string;
    mesto?: { uzel: 'pozicia' | 'oplata' | 'sostav' | 'stupen' | 'net'; pole: string | null };
    pozicii?: Array<{
      id: number;
      title: string;
      cena_display: string | null;
      prays_display: string | null;
      note: string | null;
    }>;
  }>;
  caption?: string | null;
}

export interface ZakrytieScreen {
  booking: ZapisRow;
  check: CheckFull | null;
  lines: CheckLine[];
  rollover_gate?: ZakrytieScreen['gate'] | null;
  gate: {
    can_close: boolean;
    blockers: string[];
    /**
     * Каждое условие со своим ключом, признаком и словом.
     *
     * ⛔ Галка НЕ выводится из отсутствия кода в `blockers`: условие, которое
     * гейт перестал проверять, нарисовалось бы «выполненным».
     */
    /** Сколько условий не готово — считает гейт (владелец, 20.09: строка-счётчик). */
    ne_gotovo?: number;
    kody?: string[];
    conditions: Array<{
      code: string;
      passed: boolean;
      text: string;
      /**
       * ⛔ ГДЕ ПРАВИТЬ — ЧАСТЬ УСЛОВИЯ, А НЕ ЗНАНИЕ ЭКРАНА (владелец,
       * 20.09.2026: «алерт надо выдавать там где править надо а не искать по
       * всему экрану»). `uzel` — к чему вести, `pole` — что красить.
       */
      mesto?: { uzel: 'pozicia' | 'oplata' | 'sostav' | 'stupen' | 'net'; pole: string | null };
      /**
       * ⛔ ПОЗИЦИИ, КОТОРЫЕ ДЕРЖАТ УСЛОВИЕ (владелец, 19.09.2026: «Красная
       * полоса называет позицию ПОИМЁННО»). Список считает гейт — тот же, что
       * решает, пройдено ли условие.
       */
      pozicii?: Array<{
        id: number;
        title: string;
        cena_display: string | null;
        prays_display: string | null;
        note: string | null;
      }>;
    }>;
    caption: string | null;
  };
  /**
   * Справочники причин экрана закрытия (`s408`, К5). У отмены оплаты до `s408`
   * стоял чистый лист, пока у пяти других родов был список: два разных порядка
   * спроса одного и того же на одном экране.
   */
  reasons?: { reopen?: Reason[]; restore?: Reason[]; payment_storno?: Reason[] };
  sale_chain_placeholder: string | null;
  /**
   * Э-У3 «Кто продал» (`s402`, Ф6). Цепочка СОБЫТИЙ по каждой позиции: у одной
   * строки чека продавцов бывает несколько и в разное время — администратор
   * записал, мастер в кресле поднял до дорогой услуги. Сумма у события СВОЯ:
   * у апгрейда это разница, а не цена позиции.
   */
  sale_chain?: SobytieProdazhi[];
  /** `s336`: из кого выбирать продавца — тот же список, что и в карточке. */
  prodavcy_spisok?: KandidatVProdavcy[];
  /** `s521`: приём денег в окне закрытия — способы, принятое, что осталось. */
  oplata?: OplataZakrytiya | null;
  /** Н-11: бронь этого визита — что зачтётся и что вернётся, словами (`s317`). */
  bron?: BronZakrytiya | null;
  /**
   * Готовое предложение следующей записи (`s318`, владелец 11.09.2026).
   * `null` — визит без слота или без мастера: предлагать нечего и врать нельзя.
   */
  sleduyushchaya?: SleduyushchayaPredlozhenie | null;
  hints: Hints;
  words: ScreenWords;
  priyomka?: PriyomkaBlok;
}

export interface SvobodnoeOkno {
  start: string;
  end: string;
  display: string;
  den_display: string;
}

export interface SleduyushchayaPredlozhenie {
  /**
   * `s966` D6: `vstayot=false` — предложение не встаёт в смену мастера и
   * свободного окна рядом нет; кружком такое время не ставится.
   */
  predlozhenie: { start: string; end: string; start_display: string; osnovanie: string; vstayot?: boolean };
  okna: SvobodnoeOkno[];
  dlitelnost_minut: number;
  dlitelnost_display: string;
  staff_display: string | null;
  /** Чем «свободно» считается сегодня — граница названа вслух, не умолчанием. */
  granica: string;
}

export async function fetchZakrytie(id: number): Promise<ZakrytieScreen> {
  const { data } = await apiClient.get<ZakrytieScreen>(`/zapis/${id}/zakrytie`);
  return data;
}

// ── `s521`: деньги-минимум ко дню X ──────────────────────────────────────────
//
// ⛔ ЧЕТЫРЕ ДВЕРИ, А НЕ ОДНА, И ЭТО РАЗНЫЕ ПОСТУПКИ: открыть чек · принять деньги ·
// подтвердить приход перевода · отменить ошибочную оплату. У последней своё право.

export async function postOtkrytChek(bookingId: number): Promise<ZakrytieScreen> {
  const { data } = await apiClient.post<ZakrytieScreen>(`/zapis/${bookingId}/chek`);
  return data;
}

/**
 * ⛔ СУММА УХОДИТ КАК ВВЕДЕНА, В РУБЛЯХ СТРОКОЙ, А КОПЕЙКИ СЧИТАЕТ СЕРВЕР.
 *
 * Перевести рубли в копейки на экране значило бы завести вторую арифметику одной
 * величины — ровно то, что запрещает договор экранов (`s311`). `null` значит
 * «весь остаток»: сколько это, знает тот же код, который принимает деньги.
 */
export async function postPrinyatOplatu(
  bookingId: number,
  telo: CashPaymentBody,
): Promise<ZakrytieScreen> {
  const { data } = await apiClient.post<ZakrytieScreen>(`/zapis/${bookingId}/oplata`, telo);
  return data;
}

export interface CashPaymentBody {
  kassa_id?: number; summa: string | null; comment?: string;
  method?: 'cash' | 'certificate' | 'bonus'; certificate_number?: string;
  request_key?: string; surplus?: 'deposit' | 'change'; change_register_id?: number; rollover_hold?: boolean;
}

export interface BookingRefundData {
  enabled: boolean; amount_rubles: string; provider_display: string | null;
  reasons: Array<{code: string; text: string}>;
  methods: Array<{id: number; title: string}>;
  requests: Array<{id: number; amount: string; status: string; method: string | null; pending: boolean}>;
}
export async function fetchBookingRefund(id: number): Promise<BookingRefundData> {
  return (await apiClient.get<BookingRefundData>(`/zapis/${id}/vozvrat-broni`)).data;
}
export async function postBookingRefund(id: number, body: {
  summa: string; kassa_id?: number; reason_code?: string; comment?: string; request_key: string;
}): Promise<BookingRefundData> {
  return (await apiClient.post<BookingRefundData>(`/zapis/${id}/vozvrat-broni`, body)).data;
}

export async function postPodtverditOplatu(bookingId: number, documentId: number): Promise<ZakrytieScreen> {
  const { data } = await apiClient.post<ZakrytieScreen>(`/zapis/${bookingId}/oplata/${documentId}/podtverdit`);
  return data;
}

/**
 * ⛔ ПРИЧИНА ЕДЕТ НОМЕРОМ СПРАВОЧНИКА, СЛОВА — ДОБАВКОЙ (`s408`, К5). Отправь
 * один текст — и «ошиблись» с «ошибка в сумме» снова стали бы двумя причинами,
 * а речь о деньгах.
 */
export async function postStornoOplaty(
  bookingId: number,
  documentId: number,
  telo: { reason_id?: number; prichina?: string },
): Promise<ZakrytieScreen> {
  const { data } = await apiClient.post<ZakrytieScreen>(
    `/zapis/${bookingId}/oplata/${documentId}/storno`,
    telo,
  );
  return data;
}

// ── Э-З4 «Ожидание окна» ─────────────────────────────────────────────────────

export interface OzhidanieScreen {
  filial_id: number | null;
  /** Имя филиала для шапки; «Вся сеть», когда фильтр не сужен. */
  filial_display: string;
  rows: ZapisRow[];
  counts: { total: number; total_display: string };
  filters: { filials: FilialOption[] };
  empty_caption: string | null;
  hints: Hints;
  words: ScreenWords;
  priyomka?: PriyomkaBlok;
}

export async function fetchOzhidanie(filialId: number | null): Promise<OzhidanieScreen> {
  const { data } = await apiClient.get<OzhidanieScreen>('/zapis/ozhidanie', {
    params: { filial_id: filialId ?? undefined },
  });
  return data;
}


/**
 * Счёт выдачи списка (`s305`, §3.3).
 *
 * ⛔ «ЕЩЁ ЕСТЬ» СЧИТАЕТ СЕРВЕР, А НЕ ЭКРАН СРАВНЕНИЕМ С СОТНЕЙ. Список ровно в
 * сто строк при сотне всего — это конец списка, а не упёршаяся страница:
 * сравни экран сам — и кнопка «Показать ещё» появилась бы там, где за ней
 * ничего нет.
 */
export interface Stranica {
  /** Есть ли строки за пределами показанных. */
  more: boolean;
  /** Сколько просить в следующий раз. */
  next: number;
  /** Ещё есть, но упёрлись в потолок: экран обязан сказать это словами. */
  at_max: boolean;
  /** Сколько строк запрошено сейчас. */
  limit: number;
}

// ── Э-З6 «Чек без записи» ────────────────────────────────────────────────────

export interface RoznicaScreen {
  ot: string;
  do: string;
  /** Период словами для шапки: «09.08.2026 — 08.09.2026». */
  period_display: string;
  filial_id: number | null;
  filial_display: string;
  columns: Column[];
  rows: CheckFull[];
  counts: { total: number; total_display: string; shown: number; shown_display: string } & Stranica;
  filters: { filials: FilialOption[] };
  empty_caption: string | null;
  hints: Hints;
  words: ScreenWords;
  priyomka?: PriyomkaBlok;
}

export interface RoznicaQuery {
  ot?: string;
  do?: string;
  filial_id?: number | null;
  /** Сколько строк показать: окно растёт нажатием «Показать ещё». */
  pokazat?: number;
}

export async function fetchRoznica(q: RoznicaQuery): Promise<RoznicaScreen> {
  const { data } = await apiClient.get<RoznicaScreen>('/zapis/roznica', {
    params: { ot: q.ot, do: q.do, filial_id: q.filial_id ?? undefined, pokazat: q.pokazat },
  });
  return data;
}

/**
 * «НОВАЯ ПРОДАЖА» БЕЗ ЗАПИСИ (`s966`). Филиал называется явно — тот, что показан
 * на экране списка: чек и касса живут в одном филиале.
 */
export interface ProdavecRoznicy {
  rod: 'staff' | 'user';
  id: number;
  imya: string;
}

export interface RoznicaNovaya {
  filial_id: number;
  prodavcy: ProdavecRoznicy[];
  sposoby: { id: number; title: string; po_umolchaniyu: boolean }[];
}

export async function fetchRoznicaNovaya(filialId: number): Promise<RoznicaNovaya> {
  const { data } = await apiClient.get<RoznicaNovaya>('/zapis/roznica/novaya', { params: { filial_id: filialId } });
  return data;
}

export async function fetchRoznicaTovary(filialId: number, q: string): Promise<{ rows: UslugaSpravochnika[] }> {
  const { data } = await apiClient.get<{ rows: UslugaSpravochnika[] }>('/zapis/roznica/tovary', {
    params: { filial_id: filialId, q },
  });
  return data;
}

export interface RaschetKorziny {
  stroki: { id: number; title: string; amount: number; cost_display: string }[];
  k_oplate_kopeks: number;
  k_oplate_display: string;
}

/** Строки и «К оплате» корзины считает сервер — экран рубли не складывает. */
export async function postRoznicaRaschet(filialId: number, tovary: { id: number; amount: number }[]): Promise<RaschetKorziny> {
  const { data } = await apiClient.post<RaschetKorziny>('/zapis/roznica/raschet', { tovary }, { params: { filial_id: filialId } });
  return data;
}

export interface ProdazhaTelo {
  customer_id: number | null;
  prodavec_rod: 'staff' | 'user';
  prodavec_id: number;
  /** Цену называет сервер по справочнику — экран шлёт только номер и количество. */
  tovary: { id: number; amount: number }[];
  kassa_id: number;
  request_key: string;
}

export async function postRoznicaProdat(filialId: number, telo: ProdazhaTelo): Promise<ChekOkno> {
  const { data } = await apiClient.post<ChekOkno>('/zapis/roznica', telo, { params: { filial_id: filialId } });
  return data;
}

// ── Э-З7 «Незакрытое» ────────────────────────────────────────────────────────

export interface NezakrytoeScreen {
  filial_id: number | null;
  filial_display: string;
  manager: ZapisRow[];
  debts: ZapisRow[];
  counts: {
    manager_total: number;
    manager_display: string;
    /** Сколько показано в этом списке — своё число у каждого из двух. */
    manager_shown: number;
    manager_shown_display: string;
    debts_total: number;
    debts_display: string;
    debts_shown: number;
    debts_shown_display: string;
    /** Н-39: величина со своим словом — «1 у управляющей», «4 ждут оплаты». */
    manager_svodka_display?: string | null;
    debts_svodka_display?: string | null;
    manager_page: Stranica;
    debts_page: Stranica;
  };
  filters: {
    filials: FilialOption[];
    /** `s342`: мастер и даты — те же фильтры, что у «Без исхода»; тип дыры — вкладки. */
    staff?: StaffOption[];
    vybrano?: { staff_id: number | null; ot: string | null; do: string | null };
  };
  /**
   * Кассы по филиалам — куда класть деньги при закрытии со строки (`s319`).
   *
   * ⛔ ПО ФИЛИАЛАМ, А НЕ ОДНИМ СПИСКОМ: на «Всей сети» в колонках лежат записи
   * разных точек, и касса чужого филиала — это деньги, положенные не туда.
   */
  kassy_po_filialam: Record<number, KassaOption[]>;
  empty_caption: string | null;
  hints: Hints;
  words: ScreenWords;
  priyomka?: PriyomkaBlok;
}

export interface KassaOption {
  id: number;
  title: string;
  po_umolchaniyu: boolean;
  /** Перевод и СБП: деньги приходят позже, приход подтверждает человек рукой. */
  trebuet_podtverzhdeniya: boolean;
}

export async function fetchNezakrytoe(filialId: number | null, pokazat?: number, filtry?: BezIshodaFiltry): Promise<NezakrytoeScreen> {
  const { data } = await apiClient.get<NezakrytoeScreen>('/zapis/nezakrytoe', {
    params: {
      filial_id: filialId ?? undefined,
      pokazat,
      staff_id: filtry?.staff_id ?? undefined,
      ot: filtry?.ot || undefined,
      do: filtry?.do || undefined,
    },
  });
  return data;
}

// ── Э-З8 «Без исхода» ────────────────────────────────────────────────────────

export interface FilialCount {
  filial_id: number;
  filial_display: string;
  count: number;
}

export interface BezIshodaScreen {
  filial_id: number | null;
  filial_display: string;
  columns: Column[];
  rows: ZapisRow[];
  counts: {
    /** Во всей сети — не меняется от выбора филиала. */
    total: number;
    total_display: string;
    /** В выбранном филиале — отдельное число, сводку не подменяет. */
    in_filial: number;
    in_filial_display: string;
    /** Н-39: «12 в этом филиале» — число со своим словом. */
    in_filial_svodka_display?: string | null;
    /** Сколько строк загружено сейчас. */
    shown: number;
    shown_display: string;
    by_filial: FilialCount[];
  } & Stranica;
  reasons: { cancel: Reason[]; no_show: Reason[] };
  /**
   * ⛔ РОД РЕШЕНИЯ ЕДЕТ СПРАВОЧНИКОМ, А НЕ ЛЕЖИТ ДВУМЯ ПУНКТАМИ В JSX. До 09.09
   * разбор пачкой подписывал их ЧУЖИМИ ключами словаря — «Визиты клиентки» и
   * «Вся сеть», то есть заголовком раздела и именем фильтра вместо рода решения.
   */
  initiators: Initiator[];
  filters: {
    filials: FilialOption[];
    /** Фильтры экрана (О-11): мастер, даты, род дыры. */
    staff: StaffOption[];
    dyry: KodSlovo[];
    vybrano: { staff_id: number | null; ot: string | null; do: string | null; dyra: string | null };
  };
  empty_caption: string | null;
  hints: Hints;
  words: ScreenWords;
  priyomka?: PriyomkaBlok;
}

/** Фильтры экрана «Незакрытые записи» (О-11). */
export interface BezIshodaFiltry {
  staff_id?: number | null;
  ot?: string | null;
  do?: string | null;
  dyra?: string | null;
}

export async function fetchBezIshoda(
  filialId: number | null,
  pokazat?: number,
  filtry?: BezIshodaFiltry,
): Promise<BezIshodaScreen> {
  const { data } = await apiClient.get<BezIshodaScreen>('/zapis/bez-ishoda', {
    params: {
      filial_id: filialId ?? undefined,
      pokazat,
      staff_id: filtry?.staff_id ?? undefined,
      ot: filtry?.ot || undefined,
      do: filtry?.do || undefined,
      dyra: filtry?.dyra || undefined,
    },
  });
  return data;
}

// ── Ход над записью — единственный вход, всегда через машину ─────────────────

export interface HodBody {
  refund_request_key?: string;
  refund_cash_register_id?: number;
  refund_reason_code?: string;
  refund_comment?: string;
  refund_expected_amount?: string;
  /** Н-17: вид решения по долгу — только для хода `resolve_debt`. */
  debt_resolution?: 'barter' | 'forgiven' | 'refund';
  /** Н-20/Н-11: «Следующая запись» одним ходом с закрытием визита. */
  next_slot_start?: string;
  next_slot_end?: string;
  next_staff_id?: number;
  next_rollover?: boolean;
  transition: string;
  confirmed_via?: string;
  /**
   * ⛔ КОГО СТАВИМ В ЗАПИСЬ — только для хода `smenit_klienta` (`s341`,
   * эталон Э1). Вместе с записью на нового человека переезжает её ОТКРЫТЫЙ
   * ЧЕК: иначе визит считался бы за одной, а деньги за него — за другой.
   */
  customer_id?: number;
  reason_id?: number;
  initiated_by?: string;
  note?: string;
  reason?: string;
  staff_id?: number;
  filial_id?: number;
  slot_start?: string;
  slot_end?: string;
  arrived_at?: string;
  wanted_from?: string;
  wanted_to?: string;
  wanted_time_of_day?: string;
  /** Точное время «ЧЧ:ММ» (`s328`, Ф3) — уточнение сверх части дня, не замена. */
  wanted_time?: string;
  wanted_staff_id?: number;
  /** Дело по записи на полке переносов (О-07). */
  zadacha?: string;
  warranty_exception_reason?: string;
}

/**
 * ⛔ ХОД ВОЗВРАЩАЕТ ЭКРАН ЦЕЛИКОМ, а не «ок». Дорисуй фронт новое состояние сам —
 * и владелец видел бы не то, что лежит в базе, а то, что фронт про неё думает.
 */
/** Отказ «перенос брони требует возврата» — с данными для окна возврата (409). */
function vozvratBroniIzOtkaza(error: unknown): import('./components/PerenosBroni').TransferRefundData | null {
  const e = error as { response?: { status?: number; data?: { transfer_refund?: import('./components/PerenosBroni').TransferRefundData } } };

  return e.response?.status === 409 && e.response.data?.transfer_refund ? e.response.data.transfer_refund : null;
}

/** Отказ «перенос гарантийной записи за срок требует причины исключения» (422). */
function nuzhnoIsklyucheniyeGarantii(error: unknown): boolean {
  const e = error as { response?: { status?: number; data?: { message?: string } } };

  return e.response?.status === 422 && e.response.data?.message === 'complaint_exception_reason';
}

/**
 * ⛔ `s1027` G-11: ДВА ОКНА ПЕРЕНОСА — ОДНОЙ ОБЁРТКОЙ. Перенос брони спрашивает
 * возврат (409), перенос гарантийной записи за срок — причину исключения (422).
 * Обе ветки жили только в `postHod`; правка времени, которая на другой день
 * делает тот же ход `move`, их не знала: человек видел служебный код
 * `complaint_exception_reason` вместо окна. Теперь ветки одни на обе двери.
 */
async function sOknamiPerenosa(send: (extra: Partial<HodBody>) => Promise<ZapisScreen>): Promise<ZapisScreen> {
  const sVozvratom = async (extra: Partial<HodBody>): Promise<ZapisScreen> => {
    try {
      return await send(extra);
    } catch (error) {
      const refund = vozvratBroniIzOtkaza(error);
      if (refund === null) throw error;
      const { confirmTransferRefund } = await import('./confirmTransferRefund');
      return confirmTransferRefund(refund, more => send({ ...extra, ...more }));
    }
  };

  try {
    return await sVozvratom({});
  } catch (error) {
    if (!nuzhnoIsklyucheniyeGarantii(error)) throw error;
    const { confirmWarrantyException } = await import('../complaints/confirmWarrantyException');
    return confirmWarrantyException(reason => sVozvratom({ warranty_exception_reason: reason }));
  }
}

export async function postHod(id: number, body: HodBody): Promise<ZapisScreen> {
  const send = async (extra: Partial<HodBody> = {}): Promise<ZapisScreen> =>
    (await apiClient.post<ZapisScreen>(`/zapis/${id}/hod`, { ...body, ...extra })).data;

  if (!['move', 'place'].includes(body.transition)) {
    return send();
  }

  return sOknamiPerenosa(send);
}

/**
 * ВЕРНУТЬ ЗАПИСЬ С ПОЛКИ НА ПРЕЖНЕЕ МЕСТО (`s338`, Д3).
 *
 * ⛔ ТЕЛА У ЗАПРОСА НЕТ НАМЕРЕННО: прежнее время и мастера помнит след, а не
 * экран. Пришли их отсюда — и у «как было» стало бы два источника.
 */
export async function postVernutKakBylo(id: number): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/vernut-kak-bylo`, {});
  return data;
}

// ── Режим приёмки (`s307`, §3.1 штаба) ──────────────────────────────────────

/**
 * ⛔ СТРОКА СПИСКА ОПИСЫВАЕТ ЗАПИСЬ, А НЕ ОТМЕТКУ. «Тронуто: #41283» не
 * отвечает на вопрос «что я испортил»: владелец узнаёт запись по клиентке, дню
 * и мастеру — теми же словами, какими она подписана в журнале.
 */
export interface PriyomkaStroka {
  booking_id: number;
  customer_display: string | null;
  slot_display: string | null;
  slot_date_display: string | null;
  staff_display: string | null;
  filial_display: string | null;
  state_display: string | null;
  /** Где запись стояла ДО первого тычка — туда её вернёт кнопка возврата. */
  state_do_display: string | null;
  pravilo_display: string | null;
  pravilo_text: string | null;
  touched_at_display: string | null;
  hodov: number;
}

export interface PriyomkaScreen {
  priyomka: PriyomkaBlok;
  rows: PriyomkaStroka[];
  empty_caption: string | null;
  hints: Hints;
  words: ScreenWords;
}

/**
 * Правка закреплённого поля — заметки записи (V2.11).
 *
 * ⛔ СВОЙ АДРЕС, А НЕ `postHod`. Заметка состояния не меняет и ходом графа не
 * является; послать её ходом значило бы завести в машине переход без перехода.
 */
export async function postPravka(id: number, note: string): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/pravka`, { note });
  return data;
}

/**
 * ПРАВКА ВРЕМЕНИ — ТЕМ ЖЕ АДРЕСОМ, ЧТО И ЗАМЕТКА, А НЕ ХОДОМ «ПЕРЕНЕСТИ»
 * (V2.09, `s311`).
 *
 * ⛔ ПОЧЕМУ НЕ `move`. Ход переноса растит счётчик переносов записи и требует
 * причины — он отвечает на вопрос «сколько раз клиентку двигали». Опечатка в
 * часе переносом не является; посчитай её так — и счётчик, на котором держится
 * инвариант И-З8, начнёт врать о людях.
 */
/**
 * ⛔⛔ ОКОНЧАНИЕ НЕ ШЛЁТСЯ ВОВСЕ (`s407`, Д-25, владелец 14.09.2026 — десятый
 * раз). Правило сквозное: форма времени называет ТОЛЬКО начало, конец считает
 * сервер. Здесь он двигает его на ту же величину — длина визита правкой времени
 * не меняется, для этого есть ручка на плитке.
 */
export async function postPravkaVremeni(
  id: number,
  slotStart: string,
  /* ⛔ КТО ПОПРОСИЛ СДВИНУТЬ (владелец, 19.09.2026). Не назвали — довод не
     шлётся, и сервер НЕ УГАДЫВАЕТ: счётчик «по вине клиента» стоит. */
  initiatedBy?: 'client' | 'salon',
  /* ⛔ ПРИЧИНА ИЗ СПРАВОЧНИКА (`s338`, Д4): она нужна, только если сервер
     признал сдвиг ПЕРЕНОСОМ — то есть при смене даты. Внутри дня окно её не
     спрашивает вовсе, и сюда не приезжает ничего. */
  reasonId?: number,
  note?: string,
): Promise<ZapisScreen> {
  const telo = {
    ...(initiatedBy === undefined ? {} : { initiated_by: initiatedBy }),
    ...(reasonId === undefined ? {} : { reason_id: reasonId }),
    ...(note === undefined || note === '' ? {} : { note }),
  };

  try {
    const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/pravka`, { slot_start: slotStart, ...telo });
    return data;
  } catch (error) {
    /*
     * ⛔ `s1027` G-11: сдвиг на другой день сервер делает ходом `move` — и тот же
     * ход спрашивает исключение гарантии или возврат брони. Дверь правки этих
     * полей не принимает, поэтому вопрос переходит в дверь хода — с теми же
     * окнами, что у переноса (`sOknamiPerenosa`), и тем же мастером.
     */
    if (!nuzhnoIsklyucheniyeGarantii(error) && vozvratBroniIzOtkaza(error) === null) throw error;
    return postHod(id, { transition: 'move', slot_start: slotStart, ...telo });
  }
}

/**
 * ПЕРЕНЯТЬ СОСТАВ ПРЕЖНЕЙ СИСТЕМЫ В СВОЙ (`s407`, находка 8 владельца).
 *
 * ⛔ Позиции зеркала переезжают в чек как есть — имя, количество, цена, скидка;
 * дальше состав правится штатной строкой таблицы. Прежде единственной дверью
 * было «добавить услугу», и она МОЛЧА уносила прежние позиции с экрана.
 */
export async function postSostavPerenyat(id: number): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/sostav/perenyat`, {});
  return data;
}

/**
 * НОВАЯ ДЛИТЕЛЬНОСТЬ — ПРАВКОЙ КОНЦА, А НЕ ПЕРЕНОСОМ (`s311`).
 *
 * ⛔ РАСТЯНУТЬ ВИЗИТ — НЕ ЗНАЧИТ ДВИНУТЬ КЛИЕНТКУ. Ход переноса растит счётчик
 * `transfer_no`, на котором держится И-З8; посчитай им растягивание высоты — и
 * запись, которой просто добавили полчаса, стала бы «перенесённой».
 */
export async function postPravkaKonca(id: number, slotEnd: string): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/pravka`, { slot_end: slotEnd });
  return data;
}

/** «В кресле была» — ввод фактического гостя (V2.39). */
export async function postPravkaGostya(id: number, customerId: number | null): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/pravka`, {
    attendee_customer_id: customerId,
  });
  return data;
}

/**
 * ПОЖЕЛАНИЕ К МАСТЕРУ — ВЫБОР АДМИНИСТРАТОРА (`s329`, Д6).
 *
 * ⛔ ТА ЖЕ ДВЕРЬ `pravka`, ЧТО И У ОСТАЛЬНЫХ ПОЛЕЙ ЗАПИСИ: заводить свою ради
 * одного поля значило бы завести и второй порядок следа. Сервер сам отмечает
 * выбор как РУЧНОЙ — синхронизация вендора его больше не перезапишет.
 */
export async function postPozhelanieMastera(id: number, kod: 'specific' | 'any'): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/pravka`, {
    master_preference: kod,
  });
  return data;
}

/** Живой состав: добавить позицию — услугу или товар (О-19). */
export async function postSostav(id: number, telo: SostavDobavka): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/sostav`, telo);
  return data;
}

/**
 * `s401`: поставить «на что записана» и, если попросили, окончание по норме.
 *
 * ⛔ Окончание считает СЕРВЕР. В браузере не вычитается ни минуты — ровно эту
 * ошибку поймал собственный сторож в `s318`.
 */
export async function postProceduru(
  id: number,
  procedureId: number,
  postavitOkonchanie: boolean,
): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/procedura`, {
    procedure_id: procedureId,
    postavit_okonchanie: postavitOkonchanie,
  });
  return data;
}

/** Живой состав: снять позицию. Позиция уходит в корзину, не стирается. */
export async function deleteSostav(id: number, stroka: number, rod: SostavRod, prichina?: string): Promise<ZapisScreen> {
  // `s320` Д56: причина необязательна — сервер подставит след сам.
  const { data } = await apiClient.delete<ZapisScreen>(`/zapis/${id}/sostav/${stroka}`, {
    data: prichina && prichina.trim() !== '' ? { kind: rod, reason: prichina } : { kind: rod },
  });
  return data;
}

/**
 * Живой состав: правка строки — количество и скидка (`s404`, Д-34).
 *
 * ⛔ СУММА НЕ ШЛЁТСЯ: её считает сервер. Экран называет только то, что ввёл
 * человек, — иначе у одной величины появилось бы два автора.
 */
export async function patchSostav(
  id: number,
  stroka: number,
  telo: SostavPravka,
): Promise<ZapisScreen> {
  const { data } = await apiClient.patch<ZapisScreen>(`/zapis/${id}/sostav/${stroka}`, telo);
  return data;
}

/** Справочник услуг филиала с поиском — строка подбора в карточке (V2.24). */
export async function fetchSpravochnikUslug(
  id: number,
  q: string,
  /** ⛔ `s329` Д29: номер ЗАМЕНЯЕМОЙ процедуры — сервер отберёт её категорию. */
  zamena?: number | null,
): Promise<{ rows: UslugaSpravochnika[] }> {
  const { data } = await apiClient.get<{ rows: UslugaSpravochnika[] }>(`/zapis/${id}/uslugi-spravochnik`, {
    params: zamena ? { q, zamena } : { q },
  });
  return data;
}

/**
 * Справочник ТОВАРОВ филиала с поиском (`s316`, `kau1` Y23).
 *
 * ⛔ СВОЯ ДВЕРЬ, А НЕ ПАРАМЕТР К УСЛУГАМ. Услугу ВЫПОЛНЯЮТ, товар ПРОДАЮТ — это
 * разные колонки, разная цена и разная себестоимость; сведи их одной ручкой с
 * параметром «род» — и первый же забытый параметр снова положит крем в услуги.
 */
export async function fetchSpravochnikTovarov(id: number, q: string): Promise<{ rows: UslugaSpravochnika[] }> {
  const { data } = await apiClient.get<{ rows: UslugaSpravochnika[] }>(`/zapis/${id}/tovary-spravochnik`, {
    params: { q },
  });
  return data;
}

/**
 * ДВЕ ДВЕРИ БРОНИ И ПОЖЕЛАНИЙ (`s317`, Н-06 / Н-28).
 *
 * ⛔ ОТДЕЛЬНЫЕ АДРЕСА, А НЕ ПАРАМЕТРЫ К `/hod`. Ни «хочу раньше», ни отсрочка
 * состояния записи не меняют — ходами графа они не являются, и в общем входе
 * машины завели бы второй смысл (тот же довод, что у `pravka`).
 */
export async function postHochuRanshe(id: number, telo: HochuRansheTelo): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/hochu-ranshe`, telo);
  return data;
}

/**
 * ПОМЕТИТЬ ПОДСТАТУС ПОДТВЕРЖДЕНИЯ РУКОЙ (`s324`).
 *
 * ⛔ СВОЙ АДРЕС, А НЕ `/hod`: запись как была подтверждена, так и остаётся —
 * человек уточняет, КАКИМ способом это произошло. Перехода состояний нет.
 * `null` — снять пометку и вернуть решение системе.
 */
export async function postPodstatus(id: number, podstatus: string | null): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/podstatus`, { podstatus });
  return data;
}

/**
 * ПОМЕТИТЬ «ПРИНЯТА БЕЗ БРОНИ» РУКОЙ (`s328`, Ф1).
 *
 * ⛔ СВОЙ АДРЕС, ДО ПОДТВЕРЖДЕНИЯ — та же причина, что у `postPodstatus`
 * строкой выше, только на более раннем участке шкалы.
 */
/**
 * ВНЕСТИ / ПРАВИТЬ / СНЯТЬ БРОНЬ ИЗ КАРТОЧКИ (владелец, 19.09.2026).
 *
 * До X — отметка вендора. После X сервер принимает кассовую пару и хол атомарно.
 * Ключ формы защищает повтор, включая запоздавший запрос после возврата.
 */
export type BronTelo =
  | { snyat: true }
  | { snyat: false; summa_rubles: number; cash_register_id: number; data: string; request_key?: string };

export async function postBron(id: number, telo: BronTelo): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/bron`, telo);
  return data;
}

export async function postPrinyataBezBroni(id: number, prinyata: boolean): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/prinyata`, { prinyata });
  return data;
}

export async function postOtsrochkaBroni(id: number, telo: OtsrochkaTelo): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/otsrochka-broni`, telo);
  return data;
}

/** Предзапись как ДЕЙСТВИЕ: создание будущей записи из карточки (О-23). */
export async function postPredzapis(id: number, telo: PredzapisTelo): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(`/zapis/${id}/predzapis`, telo);
  return data;
}

export async function fetchPriyomka(): Promise<PriyomkaScreen> {
  const { data } = await apiClient.get<PriyomkaScreen>('/zapis/priyomka');
  return data;
}

/**
 * «Наполнить примером» и «Снести примеры» (О-15).
 *
 * ⛔ ОБА ЖИВУТ ТОЛЬКО В РЕЖИМЕ ПРИЁМКИ и отказывают вне его — проверку держит
 * сервер, а не спрятанная кнопка.
 */
export async function postPrimer(ekran: string): Promise<PriyomkaScreen> {
  const { data } = await apiClient.post<PriyomkaScreen>('/zapis/priyomka/primer', { ekran });
  return data;
}

export async function postPrimerSnesti(): Promise<PriyomkaScreen> {
  const { data } = await apiClient.post<PriyomkaScreen>('/zapis/priyomka/primer-snesti');
  return data;
}

export async function postPriyomka(enabled: boolean): Promise<PriyomkaScreen> {
  const { data } = await apiClient.post<PriyomkaScreen>('/zapis/priyomka', { enabled });
  return data;
}

/**
 * Подтвердить цепочку «кто продал» у позиции чека (`s318`, `kau1` Y44).
 *
 * ⛔ ДВЕРИ НЕ БЫЛО, А ГЕЙТ ЗАКРЫТИЯ ЭТО ТРЕБОВАЛ: позиция без авто-подстановки
 * делала визит незакрываемым навсегда.
 */
export async function podtverditCepochku(bookingId: number, stroka: number): Promise<ZakrytieScreen> {
  const { data } = await apiClient.post<ZakrytieScreen>(`/zapis/${bookingId}/sostav/${stroka}/cepochka`);
  return data;
}

/**
 * ⛔ ТО ЖЕ ПОДТВЕРЖДЕНИЕ, НО ИЗ КАРТОЧКИ (`s336`). Сервер собирает тот экран,
 * с которого нажали: верни он всегда «закрытие», карточка перерисовалась бы
 * ЧУЖИМ экраном либо потребовала второго запроса за своим.
 */
export async function podtverditProdavcaVKartochke(bookingId: number, stroka: number): Promise<ZapisScreen> {
  const { data } = await apiClient.post<ZapisScreen>(
    `/zapis/${bookingId}/sostav/${stroka}/cepochka`, { ekran: 'zapis' },
  );
  return data;
}

/**
 * ⛔⛔ ПОСТАВИТЬ ПРОДАВЦА РУКОЙ (`s336`, слово владельца 19.09.2026).
 * Двери не было ни на экране, ни в API, ни в коде: за 90 дней в бою 25
 * подтверждений и НОЛЬ исправлений — поправить догадку было нечем.
 */
export async function postavitProdavca(
  bookingId: number, stroka: number,
  telo: { rod: ProdavecRod; id: number | null; ekran: 'zapis' },
): Promise<ZapisScreen>;
export async function postavitProdavca(
  bookingId: number, stroka: number,
  telo: { rod: ProdavecRod; id: number | null; ekran: 'zakrytie' },
): Promise<ZakrytieScreen>;
export async function postavitProdavca(
  bookingId: number, stroka: number,
  telo: { rod: ProdavecRod; id: number | null; ekran: 'zapis' | 'zakrytie' },
): Promise<ZapisScreen | ZakrytieScreen> {
  const { data } = await apiClient.post(`/zapis/${bookingId}/sostav/${stroka}/prodavec`, telo);
  return data;
}

/**
 * ⛔ `s966` D5: ПРОДАВЕЦ ТОВАРА — своя дверь. Владелец 25.10.2026: товар продаёт
 * мастер ИЛИ администратор («Админу платить, проценты такие же если продал именно
 * админ»), поэтому род — `staff` или `user`; «никому» у товара нет.
 */
export async function postavitProdavcaTovara(
  bookingId: number, stroka: number, rod: 'staff' | 'user', kto: number,
): Promise<ZakrytieScreen> {
  const { data } = await apiClient.post<ZakrytieScreen>(
    `/zapis/${bookingId}/tovar/${stroka}/prodavec`, { rod, id: kto, ekran: 'zakrytie' },
  );
  return data;
}

/**
 * УДАЛИТЬ ЗАПИСЬ (`s320`, Д13) — не то же, что отменить.
 *
 * ⛔ Причина здесь НЕОБЯЗАТЕЛЬНА: спрашивать её у ошибочно заведённой записи
 * незачем, а у состоявшегося визита её потребует сам сервер — граница «состоялся
 * ли визит» живёт в расчётном слое, а не в браузере.
 */
export async function deleteZapis(id: number, prichina?: string): Promise<void> {
  await apiClient.delete(`/zapis/${id}`, {
    data: prichina && prichina.trim() !== '' ? { reason: prichina } : {},
  });
}

/**
 * КРАСНЫЕ МЕТКИ ПУНКТОВ МЕНЮ «ЗАПИСЬ» (`s329`, Д28).
 *
 * ⛔ ЧИСЛА СЧИТАЕТ СЕРВЕР ТЕМ ЖЕ ПРЕДИКАТОМ, ЧТО И САМ СПИСОК. Своего счёта в
 * браузере здесь быть не может: он видел бы только выданную страницу, а метка
 * отвечает за ВСЁ содержимое пункта.
 */
export async function fetchMetkiZapisi(): Promise<Record<string, number>> {
  const { data } = await apiClient.get<Record<string, number>>('/zapis/metki');
  return data;
}

/**
 * ЗАВЕСТИ КЛИЕНТКУ ИЗ ОКНА НОВОЙ ЗАПИСИ (владелец, 19.09.2026, живая приёмка:
 * «БАГ. записываю нового клиента, логично что он не находит его - не даёт
 * создать»).
 *
 * ⛔ СВОЯ ДВЕРЬ С ПРАВОМ ЗАПИСИ. Серверная `POST /customers` для этого уже
 * была, но панель её не звала НИ ОДНИМ компонентом, а её право
 * (`customers.manage`) даёт ещё и правку с удалением карточек.
 *
 * ⛔ ТОТ ЖЕ НОМЕР НАХОДИТ ТУ ЖЕ КЛИЕНТКУ: опознание на сервере идёт резолвером,
 * дубль не плодится. `sozdana` честно говорит, завели нового человека или нашли.
 */
export async function postNovayaKlientka(telo: {
  phone_display: string;
  display_name?: string;
  filial_id?: number;
}): Promise<{ id: number; display_name: string; phone_display: string; sozdana: boolean }> {
  const { data } = await apiClient.post<{ id: number; display_name: string; phone_display: string; sozdana: boolean }>(
    '/zapis-klientka',
    telo,
  );
  return data;
}


/**
 * ПЕРЕПИСКА С КЛИЕНТКОЙ В КАРТОЧКЕ ЗАПИСИ (`s341`, ПР-47).
 *
 * ⛔⛔ ЭТА ДВЕРЬ ОТДАЁТ НОМЕР ОБРАЩЕНИЯ, А НЕ ЛЕНТУ. Ленту и отправку карточка
 * берёт теми же вызовами `features/obrashcheniya`, что и экран обращений:
 * один писатель, один счёт доставки, одно правило окна WhatsApp. Своё чтение
 * здесь завело бы второй ответ на вопрос «что клиентке написали».
 */
export interface PerepiskaZapisi {
  /** Номер обращения, чью ленту показать, или `null`. */
  obrashchenie_id: number | null;
  /** Чья запись — хроника клиентки видна и без разговора (`s619`). */
  customer_id?: number | null;
  /** Почему ленты нет — готовой фразой. Три разные беды, не одна. */
  pochemu: string | null;
  /** Куда можно написать первым: есть И адрес у клиентки, И наш канал. */
  kuda_pisat: Array<{ kind: string; kind_display: string; channel_id: number }>;
  /** `s720`: все наши линии для первого сообщения — номером и принадлежностью; прежний сервер поля не шлёт. */
  linii?: Array<{ channel_id: number; kind: string; nazvanie: string; podpis: string; filial: string }>;
}

export async function fetchPerepiskaZapisi(bookingId: number): Promise<PerepiskaZapisi> {
  const { data } = await apiClient.get<PerepiskaZapisi>(`/zapis/${bookingId}/perepiska`);

  return data;
}

export async function nachatPerepisku(
  bookingId: number,
  kind: string,
  channelId?: number | null,
): Promise<PerepiskaZapisi> {
  const { data } = await apiClient.post<PerepiskaZapisi>(`/zapis/${bookingId}/perepiska`, {
    kind,
    channel_id: channelId ?? null,
  });

  return data;
}
